import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/auth-context";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "@/hooks/use-toast";
import { PageHeader } from "@/components/shared/PageHeader";
import { RefreshingIndicator } from "@/components/shared/RefreshingIndicator";
import { Skeleton } from "@/components/ui/skeleton";
import { apiRequest } from "@/lib/api";
import { updateCompanySettings, type CompanySettings } from "@/lib/company";
import {
  createTemplate,
  deleteTemplate,
  fetchTemplates,
  setTemplateDefault,
  updateTemplate,
  type ApiTemplate,
  type TemplateCategory,
} from "@/lib/templates";
import {
  archivePriceBookItem,
  createPriceBookItem,
  importDefaultPriceBookItems,
  removePriceBookItem,
  updatePriceBookItem,
  type PriceBookItem,
  type PriceBookItemType,
  type PriceBookPricingMode,
} from "@/lib/price-book";
import { PRICE_BOOK_UNIT_OPTIONS } from "@/lib/price-book-units";
import {
  createProjectLabel,
  deleteProjectLabel,
  updateProjectLabel,
  type ProjectLabel,
} from "@/lib/projects-clients";
import {
  invalidatePriceBookData,
  invalidateProjectLabelData,
} from "@/lib/query-invalidation";
import {
  Building2,
  Hash,
  Bell,
  CalendarRange,
  Palette,
  Mail,
  Shield,
  CreditCard,
  Puzzle,
  CheckCircle2,
  Upload,
  Download,
  Save,
  Loader2,
  GripVertical,
  Package,
  Tags,
  Search,
  Pencil,
  Archive,
  Trash2,
  MoreHorizontal,
  ChevronDown,
  Star,
  Plus,
  Info,
} from "lucide-react";
import {
  useCompanyMeQuery,
  useCompanySettingsQuery,
  usePriceBookItems,
  useProjectLabels,
  useTemplatesQuery,
} from "@/hooks/use-app-queries";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { LanguageSwitcher } from "@/components/shared/LanguageSwitcher";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const settingsTabs = [
  { id: "company", label: "Company Profile", icon: Building2 },
  { id: "branding", label: "Branding", icon: Palette },
  { id: "numbering", label: "Ticket Types & Numbering", icon: Hash },
  { id: "projectlabels", label: "Project Labels", icon: Tags },
  { id: "approvals", label: "Approval & Reminders", icon: CheckCircle2 },
  { id: "scheduling", label: "Scheduling", icon: CalendarRange },
  { id: "emailtemplates", label: "Email Templates", icon: Mail },
  { id: "notifications", label: "Notifications", icon: Bell },
  { id: "security", label: "Security", icon: Shield },
  { id: "billing", label: "Billing", icon: CreditCard },
  { id: "pricebook", label: "Price Book", icon: Package },
  { id: "integrations", label: "Integrations", icon: Puzzle },
] as const;

type CompanyProfile = {
  id: string;
  name: string;
  ownerFullName: string;
  email: string;
  phone?: string | null;
  website?: string | null;
  companyType?:
    | "GENERAL_CONTRACTOR_REMODELER"
    | "PLUMBING_CONTRACTOR"
    | "ELECTRICAL_CONTRACTOR"
    | "HVAC_CONTRACTOR"
    | "LOW_VOLTAGE_SECURITY_ACCESS_CONTROL_CONTRACTOR"
    | "DRYWALL_FRAMING_CARPENTRY_CONTRACTOR"
    | "FLOORING_CONTRACTOR"
    | "PAINTING_CONTRACTOR"
    | "CONCRETE_CONTRACTOR"
    | "ROOFING_CONTRACTOR"
    | "LANDSCAPING_HARDSCAPING_CONTRACTOR"
    | null;
};

type FormState = {
  companyName: string;
  ownerFullName: string;
  companyType:
    | "GENERAL_CONTRACTOR_REMODELER"
    | "PLUMBING_CONTRACTOR"
    | "ELECTRICAL_CONTRACTOR"
    | "HVAC_CONTRACTOR"
    | "LOW_VOLTAGE_SECURITY_ACCESS_CONTROL_CONTRACTOR"
    | "DRYWALL_FRAMING_CARPENTRY_CONTRACTOR"
    | "FLOORING_CONTRACTOR"
    | "PAINTING_CONTRACTOR"
    | "CONCRETE_CONTRACTOR"
    | "ROOFING_CONTRACTOR"
    | "LANDSCAPING_HARDSCAPING_CONTRACTOR";
  companyEmail: string;
  companyPhone: string;
  companyAddress: string;
  companyWebsite: string;
  companyLogoUrl: string;
  companySealSignatureUrl: string;
  pdfBrandTagline: string;
  pdfBrandColorHex: string;
  currencyCode: string;

  changeOrderEnabled: boolean;
  tmTicketEnabled: boolean;
  extraWorkEnabled: boolean;
  ticketTypeOrder: Array<"co" | "tm" | "ew">;
  coPrefix: string;
  tmPrefix: string;
  extraWorkPrefix: string;
  coNumberFormat: "PREFIX-YEAR-SEQ" | "PREFIX-SEQ";
  tmNumberFormat: "PREFIX-YEAR-SEQ" | "PREFIX-SEQ";
  ewNumberFormat: "PREFIX-YEAR-SEQ" | "PREFIX-SEQ";
  coNextNumber: number;
  tmNextNumber: number;
  ewNextNumber: number;

  autoRemindersEnabled: boolean;
  defaultReminderCadenceDays: number;
  requireRejectionComment: boolean;
  allowManualApprovalOverride: boolean;
  requireSignatureToApprove: boolean;
  autoDeletePendingApprovalEnabled: boolean;
  autoDeletePendingApprovalDays: number;

  notifyTicketApproved: boolean;
  notifyTicketRejected: boolean;
  notifySignatureCaptured: boolean;
  notifyReminderSent: boolean;
  notifyPaymentFollowupSent: boolean;
  notifyPaymentRecorded: boolean;
  notifyWeeklySummary: boolean;
  scheduleAutoStartEnabled: boolean;
  scheduleAutoCompleteEnabled: boolean;
  scheduleWorkdayStartTime: string;
  scheduleWorkdayEndTime: string;

  approvalLinkExpiryDays: number;
  defaultTaxRate: number;
  twoFactorEnabled: boolean;
};

type PriceBookFormState = {
  name: string;
  type: PriceBookItemType;
  description: string;
  unit: string;
  pricingMode: PriceBookPricingMode;
  defaultPrice: string;
  thumbnailUrl: string;
};

type ProjectLabelFormState = {
  name: string;
  color: string;
};

type TicketTypeKey = "tm" | "co" | "ew";
type TicketDefaultLineItemCategory =
  | "LABOR"
  | "MATERIAL"
  | "EQUIPMENT"
  | "OTHER";
type TicketDefaultLineItemPricingMethod = "HOURLY" | "FIXED";

type TicketDefaultLineItem = {
  category: TicketDefaultLineItemCategory;
  description: string;
  quantity: string;
  unit: string;
  unitCost: string;
  pricingMethod: TicketDefaultLineItemPricingMethod;
  fixedAmount: string;
  useUnit: boolean;
};

type TicketLineItemDefaultsByCategory = {
  LABOR: TicketDefaultLineItem;
  MATERIAL: TicketDefaultLineItem;
  EQUIPMENT: TicketDefaultLineItem;
  OTHER: TicketDefaultLineItem;
};

type EmailTemplateDraft = {
  id?: string;
  category: TemplateCategory;
  subType: string;
  name: string;
  isDefault: boolean;
  dataJson: Record<string, unknown>;
};

type TicketDefaultsDraft = {
  templateId: string | null;
  name: string;
  defaultTitle: string;
  defaultDescription: string;
  defaultReminderCadenceDays: string;
  lineItemDefaultsByCategory: TicketLineItemDefaultsByCategory;
  rawDataJson: Record<string, unknown>;
};

const EMPTY_PRICE_BOOK_FORM: PriceBookFormState = {
  name: "",
  type: "LABOR",
  description: "",
  unit: "",
  pricingMode: "TRACK_UNITS",
  defaultPrice: "",
  thumbnailUrl: "",
};

const PRICE_BOOK_TYPE_OPTIONS: Array<{
  value: PriceBookItemType;
  label: string;
}> = [
  { value: "LABOR", label: "Service / Labor" },
  { value: "MATERIAL", label: "Material" },
  { value: "EQUIPMENT", label: "Equipment" },
  { value: "OTHER", label: "Other" },
];

const PROJECT_LABEL_PALETTE = [
  "#F59E0B",
  "#2563EB",
  "#16A34A",
  "#DC2626",
  "#9333EA",
  "#0F766E",
  "#EA580C",
  "#475569",
];
const EMPTY_PROJECT_LABEL_FORM: ProjectLabelFormState = {
  name: "",
  color: PROJECT_LABEL_PALETTE[0],
};

const TICKET_SUBTYPE_BY_KEY: Record<TicketTypeKey, string> = {
  tm: "tm_ticket",
  co: "change_order",
  ew: "extra_work",
};

const EMAIL_TEMPLATE_SUBTYPES: Array<{ value: string; label: string }> = [
  { value: "approval_request", label: "Approval Request" },
  { value: "reminder", label: "Follow-up Reminder" },
  { value: "rejection_followup", label: "Rejection Follow-up" },
  { value: "clarification_followup", label: "Clarification Follow-up" },
  { value: "payment_followup", label: "Payment Follow-up" },
  { value: "schedule_confirmation", label: "Schedule Confirmation" },
  { value: "final_invoice", label: "Final Invoice" },
];

const createDefaultEmailTemplateData = (subType: string) => ({
  subject: "",
  body: "",
});

const TICKET_DEFAULT_TITLE_BY_KEY: Record<TicketTypeKey, string> = {
  tm: "Estimate",
  co: "Change Order Estimate",
  ew: "Extra Work Estimate",
};

const materialUnitOptions = [
  "ea",
  "box",
  "bag",
  "sheet",
  "bucket",
  "roll",
  "ft",
  "lf",
  "sq ft",
  "gal",
  "cu yd",
  "lb",
];
const equipmentUnitOptions = ["hrs", "days", "ea"];

const EMPTY_TICKET_LINE_ITEM: TicketDefaultLineItem = {
  category: "LABOR",
  description: "",
  quantity: "",
  unit: "hrs",
  unitCost: "",
  pricingMethod: "HOURLY",
  fixedAmount: "",
  useUnit: true,
};

const normalizeTicketLineItemCategory = (
  value: unknown,
): TicketDefaultLineItemCategory => {
  const normalized = String(value ?? "")
    .trim()
    .toUpperCase();
  if (normalized === "LABOR" || normalized === "WORK") return "LABOR";
  if (normalized === "MATERIAL" || normalized === "MATERIALS")
    return "MATERIAL";
  if (normalized === "EQUIPMENT") return "EQUIPMENT";
  return "OTHER";
};

const defaultUnitByCategory = (category: TicketDefaultLineItemCategory) => {
  if (category === "LABOR" || category === "EQUIPMENT") return "hrs";
  if (category === "MATERIAL") return "qty";
  return "qty";
};

const normalizeTicketLineItem = (value: unknown): TicketDefaultLineItem => {
  const item = (value ?? {}) as Record<string, unknown>;
  const pricingMethod =
    String(item.pricingMethod ?? "").toUpperCase() === "FIXED"
      ? "FIXED"
      : "HOURLY";
  const category = normalizeTicketLineItemCategory(item.category);
  return {
    category,
    description: String(item.description ?? ""),
    quantity: String(item.quantity ?? ""),
    unit: String(item.unit ?? defaultUnitByCategory(category)),
    unitCost: String(item.unitCost ?? ""),
    pricingMethod,
    fixedAmount: String(item.fixedAmount ?? ""),
    useUnit: item.useUnit === false ? false : true,
  };
};

const serializeTicketLineItems = (rows: TicketDefaultLineItem[]) =>
  rows.map((row) => ({
    category: row.category,
    description: row.description,
    quantity: row.quantity,
    unit: row.category === "MATERIAL" && !row.useUnit ? "" : row.unit,
    unitCost: row.unitCost,
    pricingMethod: row.pricingMethod,
    fixedAmount: row.fixedAmount,
    useUnit: row.useUnit,
  }));

const defaultTicketLineItemDefaultsByCategory =
  (): TicketLineItemDefaultsByCategory => ({
    LABOR: {
      ...EMPTY_TICKET_LINE_ITEM,
      category: "LABOR",
      unit: defaultUnitByCategory("LABOR"),
    },
    MATERIAL: {
      ...EMPTY_TICKET_LINE_ITEM,
      category: "MATERIAL",
      unit: defaultUnitByCategory("MATERIAL"),
    },
    EQUIPMENT: {
      ...EMPTY_TICKET_LINE_ITEM,
      category: "EQUIPMENT",
      unit: defaultUnitByCategory("EQUIPMENT"),
    },
    OTHER: {
      ...EMPTY_TICKET_LINE_ITEM,
      category: "OTHER",
      unit: defaultUnitByCategory("OTHER"),
    },
  });

const getTicketLineItemDefaultsByCategory = (
  value: unknown,
): TicketLineItemDefaultsByCategory => {
  const source =
    value && typeof value === "object"
      ? (value as Record<string, unknown>)
      : {};
  return {
    LABOR: normalizeTicketLineItem(source.LABOR),
    MATERIAL: normalizeTicketLineItem(source.MATERIAL),
    EQUIPMENT: normalizeTicketLineItem(source.EQUIPMENT),
    OTHER: normalizeTicketLineItem(source.OTHER),
  };
};

const serializeTicketLineItemDefaultsByCategory = (
  value: TicketLineItemDefaultsByCategory,
) => ({
  LABOR: serializeTicketLineItems([value.LABOR])[0],
  MATERIAL: serializeTicketLineItems([value.MATERIAL])[0],
  EQUIPMENT: serializeTicketLineItems([value.EQUIPMENT])[0],
  OTHER: serializeTicketLineItems([value.OTHER])[0],
});

const calculateTicketLineItemTotal = (row: TicketDefaultLineItem) => {
  if (row.category === "LABOR" && row.pricingMethod === "FIXED") {
    return Number(row.fixedAmount || 0);
  }
  if (row.category === "OTHER") {
    return Number(row.unitCost || 0);
  }
  return Number(row.quantity || 0) * Number(row.unitCost || 0);
};

const createEmptyTicketDefaultsDraft = (
  key: TicketTypeKey,
): TicketDefaultsDraft => ({
  templateId: null,
  name:
    key === "tm"
      ? "Estimate Default"
      : key === "co"
        ? "Change Order Default"
        : "Extra Work Default",
  defaultTitle: TICKET_DEFAULT_TITLE_BY_KEY[key],
  defaultDescription: "",
  defaultReminderCadenceDays: "",
  lineItemDefaultsByCategory: defaultTicketLineItemDefaultsByCategory(),
  rawDataJson: {},
});

const COMPANY_TYPE_OPTIONS: Array<{
  value: FormState["companyType"];
  label: string;
}> = [
  {
    value: "GENERAL_CONTRACTOR_REMODELER",
    label: "General Contractor (GC) / Remodeler",
  },
  { value: "PLUMBING_CONTRACTOR", label: "Plumbing Contractor" },
  { value: "ELECTRICAL_CONTRACTOR", label: "Electrical Contractor" },
  { value: "HVAC_CONTRACTOR", label: "HVAC Contractor" },
  {
    value: "LOW_VOLTAGE_SECURITY_ACCESS_CONTROL_CONTRACTOR",
    label: "Low Voltage / Security / Access Control Contractor",
  },
  {
    value: "DRYWALL_FRAMING_CARPENTRY_CONTRACTOR",
    label: "Drywall / Framing / Carpentry Contractor",
  },
  { value: "FLOORING_CONTRACTOR", label: "Flooring Contractor" },
  { value: "PAINTING_CONTRACTOR", label: "Painting Contractor" },
  { value: "CONCRETE_CONTRACTOR", label: "Concrete Contractor" },
  { value: "ROOFING_CONTRACTOR", label: "Roofing Contractor" },
  {
    value: "LANDSCAPING_HARDSCAPING_CONTRACTOR",
    label: "Landscaping / Hardscaping Contractor",
  },
];

const getCompanyTypeLabel = (
  t: (key: string) => string,
  value: FormState["companyType"],
) => {
  switch (value) {
    case "GENERAL_CONTRACTOR_REMODELER":
      return t("settingsPage.companyTypes.generalContractor");
    case "PLUMBING_CONTRACTOR":
      return t("settingsPage.companyTypes.plumbing");
    case "ELECTRICAL_CONTRACTOR":
      return t("settingsPage.companyTypes.electrical");
    case "HVAC_CONTRACTOR":
      return t("settingsPage.companyTypes.hvac");
    case "LOW_VOLTAGE_SECURITY_ACCESS_CONTROL_CONTRACTOR":
      return t("settingsPage.companyTypes.lowVoltage");
    case "DRYWALL_FRAMING_CARPENTRY_CONTRACTOR":
      return t("settingsPage.companyTypes.drywall");
    case "FLOORING_CONTRACTOR":
      return t("settingsPage.companyTypes.flooring");
    case "PAINTING_CONTRACTOR":
      return t("settingsPage.companyTypes.painting");
    case "CONCRETE_CONTRACTOR":
      return t("settingsPage.companyTypes.concrete");
    case "ROOFING_CONTRACTOR":
      return t("settingsPage.companyTypes.roofing");
    case "LANDSCAPING_HARDSCAPING_CONTRACTOR":
      return t("settingsPage.companyTypes.landscaping");
    default:
      return value;
  }
};

const getPriceBookTypeLabel = (
  t: (key: string) => string,
  value: PriceBookItemType,
) => {
  switch (value) {
    case "LABOR":
      return t("settingsPage.priceBook.typeLabor");
    case "MATERIAL":
      return t("settingsPage.priceBook.typeMaterial");
    case "EQUIPMENT":
      return t("settingsPage.priceBook.typeEquipment");
    case "OTHER":
      return t("settingsPage.priceBook.typeOther");
    default:
      return value;
  }
};

const getEmailSubtypeLabel = (t: (key: string) => string, value: string) => {
  switch (value) {
    case "approval_request":
      return t("settingsPage.emailTemplates.approvalRequest");
    case "reminder":
      return t("settingsPage.emailTemplates.followUpReminder");
    case "rejection_followup":
      return t("settingsPage.emailTemplates.rejectionFollowUp");
    case "clarification_followup":
      return t("settingsPage.emailTemplates.clarificationFollowUp");
    case "payment_followup":
      return t("settingsPage.emailTemplates.paymentFollowUp");
    case "schedule_confirmation":
      return t("settingsPage.emailTemplates.scheduleConfirmation");
    case "final_invoice":
      return t("settingsPage.emailTemplates.finalInvoice");
    default:
      return value;
  }
};

function SettingsLoading() {
  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row gap-6">
        <div className="md:w-56 space-y-2">
          {Array.from({ length: settingsTabs.length }).map((_, i) => (
            <Skeleton key={i} className="h-9 w-full" />
          ))}
        </div>
        <div className="flex-1 rounded-lg border bg-card p-6 space-y-3">
          <Skeleton className="h-6 w-52" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      </div>
    </div>
  );
}

const toDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export default function Settings() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const translatedSettingsTabs = settingsTabs.map((tab) => ({
    ...tab,
    label: t(`settings.tabs.${tab.id}`, { defaultValue: tab.label }),
  }));
  const { user, refreshMe } = useAuth();

  const [activeTab, setActiveTab] = useState("company");
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState<FormState | null>(null);
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [sealSignatureDataUrl, setSealSignatureDataUrl] = useState<
    string | null
  >(null);
  const [removeSealSignature, setRemoveSealSignature] = useState(false);
  const [priceBookSearch, setPriceBookSearch] = useState("");
  const [priceBookIncludeArchived, setPriceBookIncludeArchived] =
    useState(false);
  const [priceBookEditorOpen, setPriceBookEditorOpen] = useState(false);
  const [priceBookSaving, setPriceBookSaving] = useState(false);
  const [priceBookImporting, setPriceBookImporting] = useState(false);
  const [priceBookTarget, setPriceBookTarget] = useState<PriceBookItem | null>(
    null,
  );
  const [priceBookThumbnailDataUrl, setPriceBookThumbnailDataUrl] = useState<
    string | null
  >(null);
  const [priceBookRemoveThumbnail, setPriceBookRemoveThumbnail] =
    useState(false);
  const [priceBookForm, setPriceBookForm] = useState<PriceBookFormState>(
    EMPTY_PRICE_BOOK_FORM,
  );
  const [projectLabelSearch, setProjectLabelSearch] = useState("");
  const [projectLabelsHelpOpen, setProjectLabelsHelpOpen] = useState(false);
  const [projectLabelEditorOpen, setProjectLabelEditorOpen] = useState(false);
  const [projectLabelSaving, setProjectLabelSaving] = useState(false);
  const [projectLabelTarget, setProjectLabelTarget] =
    useState<ProjectLabel | null>(null);
  const [projectLabelDeleteTarget, setProjectLabelDeleteTarget] =
    useState<ProjectLabel | null>(null);
  const [projectLabelForm, setProjectLabelForm] =
    useState<ProjectLabelFormState>(EMPTY_PROJECT_LABEL_FORM);
  const [confirmCompanyTypeChangeOpen, setConfirmCompanyTypeChangeOpen] =
    useState(false);
  const [ticketDefaultsOpen, setTicketDefaultsOpen] = useState<
    Record<TicketTypeKey, boolean>
  >({
    tm: false,
    co: false,
    ew: false,
  });
  const [ticketDefaultsByType, setTicketDefaultsByType] = useState<
    Record<TicketTypeKey, TicketDefaultsDraft>
  >({
    tm: createEmptyTicketDefaultsDraft("tm"),
    co: createEmptyTicketDefaultsDraft("co"),
    ew: createEmptyTicketDefaultsDraft("ew"),
  });
  const [ticketDefaultsSavingKey, setTicketDefaultsSavingKey] =
    useState<TicketTypeKey | null>(null);
  const [ticketDefaultsLoading, setTicketDefaultsLoading] = useState(false);
  const [emailTemplateEditorOpen, setEmailTemplateEditorOpen] = useState(false);
  const [emailTemplateDraft, setEmailTemplateDraft] =
    useState<EmailTemplateDraft | null>(null);
  const [emailTemplateSaving, setEmailTemplateSaving] = useState(false);
  const [emailTemplateSubType, setEmailTemplateSubType] = useState(
    EMAIL_TEMPLATE_SUBTYPES[0].value,
  );
  const [emailFocus, setEmailFocus] = useState<"subject" | "body">("body");
  const [emailTemplateAction, setEmailTemplateAction] = useState<string | null>(
    null,
  );

  const initialSnapshotRef = useRef<string>("");
  const isAdmin = user?.role === "ADMIN";
  const readOnly = !isAdmin;
  const starterEmailTemplatesLocked = user?.currentPlan === "STARTER";
  const starterBrandingLocked = user?.currentPlan === "STARTER";
  const brandingReadOnly = readOnly || starterBrandingLocked;
  const companyQuery = useCompanyMeQuery();
  const settingsQuery = useCompanySettingsQuery();
  const priceBookQuery = usePriceBookItems({
    search: priceBookSearch,
    includeArchived: priceBookIncludeArchived,
  });
  const projectLabelsQuery = useProjectLabels();
  const emailTemplatesQuery = useTemplatesQuery({ category: "email" });
  const company = (companyQuery.data as CompanyProfile | undefined) ?? null;
  const settings = settingsQuery.data ?? null;
  const loading =
    (companyQuery.isLoading && !companyQuery.data) ||
    (settingsQuery.isLoading && !settingsQuery.data);
  const isRefreshing =
    (companyQuery.isFetching && Boolean(companyQuery.data)) ||
    (settingsQuery.isFetching && Boolean(settingsQuery.data));
  const emailTemplates = emailTemplatesQuery.data?.items ?? [];
  const emailTemplateVariables = emailTemplatesQuery.data?.variables ?? [];

  const showEmailTemplateUpgradeMessage = () => {
    toast({
      title: t("settingsPage.emailTemplates.lockedTitle"),
      description: t("settingsPage.emailTemplates.lockedDescription"),
      variant: "destructive",
    });
  };

  useEffect(() => {
    if (!starterEmailTemplatesLocked) return;
    if (!emailTemplateEditorOpen) return;
    setEmailTemplateEditorOpen(false);
    setEmailTemplateDraft(null);
  }, [starterEmailTemplatesLocked, emailTemplateEditorOpen]);

  useEffect(() => {
    if (!company || !settings || form) return;

    const initialForm: FormState = {
      companyName: company.name ?? "",
      ownerFullName: company.ownerFullName ?? "",
      companyType: company.companyType ?? "GENERAL_CONTRACTOR_REMODELER",
      companyEmail: company.email ?? "",
      companyPhone: company.phone ?? "",
      companyAddress: settings.companyAddress ?? "",
      companyWebsite: company.website ?? "",
      companyLogoUrl: settings.companyLogoUrl ?? "",
      companySealSignatureUrl: settings.companySealSignatureUrl ?? "",
      pdfBrandTagline: settings.pdfBrandTagline ?? "",
      pdfBrandColorHex: settings.pdfBrandColorHex ?? "#F59E0B",
      currencyCode: settings.currencyCode ?? "USD",

      changeOrderEnabled: settings.changeOrderEnabled,
      tmTicketEnabled: settings.tmTicketEnabled,
      extraWorkEnabled: settings.extraWorkEnabled,
      ticketTypeOrder: settings.ticketTypeOrder?.length
        ? settings.ticketTypeOrder
        : ["tm", "ew", "co"],
      coPrefix: settings.coPrefix,
      tmPrefix: settings.tmPrefix,
      extraWorkPrefix: settings.extraWorkPrefix,
      coNumberFormat: settings.coNumberFormat,
      tmNumberFormat: settings.tmNumberFormat,
      ewNumberFormat: settings.ewNumberFormat,
      coNextNumber: settings.coNextNumber,
      tmNextNumber: settings.tmNextNumber,
      ewNextNumber: settings.ewNextNumber,

      autoRemindersEnabled: settings.reminderDayOffsets.length > 0,
      defaultReminderCadenceDays: settings.defaultReminderCadenceDays,
      requireRejectionComment: settings.requireRejectionComment,
      allowManualApprovalOverride: settings.allowManualApprovalOverride,
      requireSignatureToApprove: settings.requireSignatureToApprove,
      autoDeletePendingApprovalEnabled:
        settings.autoDeletePendingApprovalEnabled ?? false,
      autoDeletePendingApprovalDays:
        settings.autoDeletePendingApprovalDays ?? 10,

      notifyTicketApproved: settings.notifyTicketApproved,
      notifyTicketRejected: settings.notifyTicketRejected,
      notifySignatureCaptured: settings.notifySignatureCaptured,
      notifyReminderSent: settings.notifyReminderSent,
      notifyPaymentFollowupSent: settings.notifyPaymentFollowupSent ?? true,
      notifyPaymentRecorded: settings.notifyPaymentRecorded ?? true,
      notifyWeeklySummary: settings.notifyWeeklySummary,
      scheduleAutoStartEnabled: settings.scheduleAutoStartEnabled ?? true,
      scheduleAutoCompleteEnabled: settings.scheduleAutoCompleteEnabled ?? true,
      scheduleWorkdayStartTime: settings.scheduleWorkdayStartTime ?? "08:00",
      scheduleWorkdayEndTime: settings.scheduleWorkdayEndTime ?? "19:00",

      approvalLinkExpiryDays: settings.approvalLinkExpiryDays,
      defaultTaxRate: Number(settings.defaultTaxRate ?? 0),
      twoFactorEnabled: settings.twoFactorEnabled,
    };

    setForm(initialForm);
    initialSnapshotRef.current = JSON.stringify(initialForm);
    setLogoDataUrl(null);
    setRemoveLogo(false);
  }, [company, settings, form]);

  const hasChanges = useMemo(() => {
    if (!form) return false;
    return (
      JSON.stringify(form) !== initialSnapshotRef.current ||
      Boolean(logoDataUrl) ||
      Boolean(sealSignatureDataUrl) ||
      removeLogo ||
      removeSealSignature
    );
  }, [
    form,
    logoDataUrl,
    removeLogo,
    removeSealSignature,
    sealSignatureDataUrl,
  ]);

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const moveTicketType = (fromIndex: number, toIndex: number) => {
    if (readOnly) return;
    setForm((prev) => {
      if (!prev) return prev;
      const nextOrder = [...prev.ticketTypeOrder];
      const [moved] = nextOrder.splice(fromIndex, 1);
      nextOrder.splice(toIndex, 0, moved);
      return { ...prev, ticketTypeOrder: nextOrder };
    });
  };

  useEffect(() => {
    if (activeTab !== "numbering") return;
    let cancelled = false;

    const loadTicketDefaults = async () => {
      setTicketDefaultsLoading(true);
      try {
        const res = await fetchTemplates({ category: "ticket" });
        if (cancelled) return;

        const next: Record<TicketTypeKey, TicketDefaultsDraft> = {
          tm: createEmptyTicketDefaultsDraft("tm"),
          co: createEmptyTicketDefaultsDraft("co"),
          ew: createEmptyTicketDefaultsDraft("ew"),
        };

        (["tm", "co", "ew"] as TicketTypeKey[]).forEach((key) => {
          const subtype = TICKET_SUBTYPE_BY_KEY[key];
          const candidates = res.items.filter(
            (item) => item.subType === subtype,
          );
          const picked =
            candidates.find((item) => item.isDefault) ?? candidates[0];
          if (!picked) return;
          const data = (picked.dataJson ?? {}) as Record<string, unknown>;
          const cadenceValue = Number(data.defaultReminderCadenceDays);
          next[key] = {
            templateId: picked.id,
            name: picked.name ?? createEmptyTicketDefaultsDraft(key).name,
            defaultTitle:
              typeof data.defaultTitle === "string" &&
              data.defaultTitle.trim().length > 0
                ? data.defaultTitle
                : TICKET_DEFAULT_TITLE_BY_KEY[key],
            defaultDescription:
              typeof data.defaultDescription === "string"
                ? data.defaultDescription
                : "",
            defaultReminderCadenceDays:
              Number.isFinite(cadenceValue) && cadenceValue > 0
                ? String(cadenceValue)
                : "",
            lineItemDefaultsByCategory: getTicketLineItemDefaultsByCategory(
              data.lineItemDefaultsByCategory,
            ),
            rawDataJson: data,
          };
        });

        setTicketDefaultsByType(next);
      } catch (err) {
        if (cancelled) return;
        toast({
          title: t("settingsPage.ticketDefaults.loadFailed"),
          description:
            err instanceof Error ? err.message : t("common.requestFailed"),
          variant: "destructive",
        });
      } finally {
        if (!cancelled) setTicketDefaultsLoading(false);
      }
    };

    void loadTicketDefaults();
    return () => {
      cancelled = true;
    };
  }, [activeTab, t]);

  const setTicketDefaultsField = <K extends keyof TicketDefaultsDraft>(
    key: TicketTypeKey,
    field: K,
    value: TicketDefaultsDraft[K],
  ) => {
    setTicketDefaultsByType((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value,
      },
    }));
  };

  const setTicketCategoryDefault = (
    key: TicketTypeKey,
    category: TicketDefaultLineItemCategory,
    patch: Partial<TicketDefaultLineItem>,
  ) => {
    setTicketDefaultsByType((prev) => {
      const current = prev[key].lineItemDefaultsByCategory;
      return {
        ...prev,
        [key]: {
          ...prev[key],
          lineItemDefaultsByCategory: {
            ...current,
            [category]: { ...current[category], ...patch },
          },
        },
      };
    });
  };

  const saveTicketDefaults = async (key: TicketTypeKey) => {
    if (readOnly) return;
    const draft = ticketDefaultsByType[key];
    const subtype = TICKET_SUBTYPE_BY_KEY[key];
    const templateName =
      draft.name.trim() || createEmptyTicketDefaultsDraft(key).name;
    const cadence = Number(draft.defaultReminderCadenceDays);
    const normalizedCadence =
      Number.isFinite(cadence) && cadence > 0 ? Math.floor(cadence) : undefined;

    const dataJson: Record<string, unknown> = {
      ...draft.rawDataJson,
      defaultTitle:
        draft.defaultTitle.trim() || TICKET_DEFAULT_TITLE_BY_KEY[key],
      defaultDescription: draft.defaultDescription,
      lineItemDefaultsByCategory: serializeTicketLineItemDefaultsByCategory(
        draft.lineItemDefaultsByCategory,
      ),
    };
    if (normalizedCadence)
      dataJson.defaultReminderCadenceDays = normalizedCadence;
    else delete dataJson.defaultReminderCadenceDays;

    setTicketDefaultsSavingKey(key);
    try {
      if (draft.templateId) {
        const updated = await updateTemplate(draft.templateId, {
          name: templateName,
          dataJson,
        });
        setTicketDefaultsByType((prev) => ({
          ...prev,
          [key]: {
            ...prev[key],
            templateId: updated.id,
            name: updated.name,
            rawDataJson: (updated.dataJson ?? {}) as Record<string, unknown>,
          },
        }));
      } else {
        const created = await createTemplate({
          category: "ticket",
          subType: subtype,
          name: templateName,
          dataJson,
          isDefault: true,
        });
        setTicketDefaultsByType((prev) => ({
          ...prev,
          [key]: {
            ...prev[key],
            templateId: created.id,
            name: created.name,
            rawDataJson: (created.dataJson ?? {}) as Record<string, unknown>,
          },
        }));
      }
      await queryClient.invalidateQueries({ queryKey: ["templates"] });
      toast({ title: t("settingsPage.ticketDefaults.saved") });
    } catch (err) {
      toast({
        title: t("settingsPage.saveFailed"),
        description:
          err instanceof Error ? err.message : t("common.requestFailed"),
        variant: "destructive",
      });
    } finally {
      setTicketDefaultsSavingKey(null);
    }
  };

  const saveChanges = async (skipCompanyTypeGuard = false) => {
    if (!form || !company || readOnly) return;

    if (!form.companyName.trim() || !form.ownerFullName.trim()) {
      toast({
        title: t("settingsPage.validation.companyAndNameRequired"),
        variant: "destructive",
      });
      return;
    }
    if (!form.companyEmail.trim() || !form.companyEmail.includes("@")) {
      toast({
        title: t("settingsPage.validation.validCompanyEmailRequired"),
        variant: "destructive",
      });
      return;
    }

    if (
      form.autoRemindersEnabled &&
      (form.defaultReminderCadenceDays < 1 ||
        form.defaultReminderCadenceDays > 30)
    ) {
      toast({
        title: t("settingsPage.validation.reminderCadence"),
        variant: "destructive",
      });
      return;
    }
    if (
      form.autoDeletePendingApprovalEnabled &&
      (form.autoDeletePendingApprovalDays < 1 ||
        form.autoDeletePendingApprovalDays > 365)
    ) {
      toast({
        title: t("settingsPage.validation.autoDeleteDays"),
        variant: "destructive",
      });
      return;
    }
    if (form.defaultTaxRate < 0 || form.defaultTaxRate > 100) {
      toast({
        title: t("settingsPage.validation.defaultTaxRate"),
        variant: "destructive",
      });
      return;
    }
    if (!/^#([A-Fa-f0-9]{6})$/.test(form.pdfBrandColorHex.trim())) {
      toast({
        title: t("settingsPage.validation.brandColorHex", {
          defaultValue: "Brand color must be a valid 6-digit hex value.",
        }),
        variant: "destructive",
      });
      return;
    }
    if (form.pdfBrandTagline.trim().length > 80) {
      toast({
        title: t("settingsPage.validation.brandTaglineLength", {
          defaultValue: "Tagline must be 80 characters or fewer.",
        }),
        variant: "destructive",
      });
      return;
    }
    if (
      !form.scheduleWorkdayStartTime ||
      !form.scheduleWorkdayEndTime ||
      form.scheduleWorkdayEndTime <= form.scheduleWorkdayStartTime
    ) {
      toast({
        title: t(
          "settingsPage.validation.scheduleWorkdayRange",
          "Workday end time must be later than the start time.",
        ),
        variant: "destructive",
      });
      return;
    }

    const existingCompanyType =
      company.companyType ?? "GENERAL_CONTRACTOR_REMODELER";
    const companyTypeChanged = form.companyType !== existingCompanyType;
    if (companyTypeChanged && !skipCompanyTypeGuard) {
      setConfirmCompanyTypeChangeOpen(true);
      return;
    }

    setSaving(true);
    try {
      await apiRequest("/companies/me", {
        method: "PATCH",
        body: {
          name: form.companyName.trim(),
          ownerFullName: form.ownerFullName.trim(),
          companyType: form.companyType,
          email: form.companyEmail.trim(),
          phone: form.companyPhone.trim() || null,
          website: form.companyWebsite.trim() || null,
        },
      });

      const updated = await updateCompanySettings({
        companyAddress: form.companyAddress.trim(),
        companyLogoDataUrl: logoDataUrl ?? undefined,
        removeCompanyLogo: removeLogo || undefined,
        currencyCode: form.currencyCode.toUpperCase(),

        changeOrderEnabled: form.changeOrderEnabled,
        tmTicketEnabled: form.tmTicketEnabled,
        extraWorkEnabled: form.extraWorkEnabled,
        ticketTypeOrder: form.ticketTypeOrder,
        coPrefix: form.coPrefix,
        tmPrefix: form.tmPrefix,
        extraWorkPrefix: form.extraWorkPrefix,
        coNumberFormat: form.coNumberFormat,
        tmNumberFormat: form.tmNumberFormat,
        ewNumberFormat: form.ewNumberFormat,
        coNextNumber: form.coNextNumber,
        tmNextNumber: form.tmNextNumber,
        ewNextNumber: form.ewNextNumber,

        defaultReminderCadenceDays: form.defaultReminderCadenceDays,
        reminderDayOffsets: form.autoRemindersEnabled
          ? [form.defaultReminderCadenceDays]
          : [],
        requireRejectionComment: form.requireRejectionComment,
        allowManualApprovalOverride: form.allowManualApprovalOverride,
        requireSignatureToApprove: form.requireSignatureToApprove,
        autoDeletePendingApprovalEnabled: form.autoDeletePendingApprovalEnabled,
        autoDeletePendingApprovalDays: form.autoDeletePendingApprovalDays,

        notifyTicketApproved: form.notifyTicketApproved,
        notifyTicketRejected: form.notifyTicketRejected,
        notifySignatureCaptured: form.notifySignatureCaptured,
        notifyReminderSent: form.notifyReminderSent,
        notifyPaymentFollowupSent: form.notifyPaymentFollowupSent,
        notifyPaymentRecorded: form.notifyPaymentRecorded,
        notifyWeeklySummary: form.notifyWeeklySummary,
        scheduleAutoStartEnabled: form.scheduleAutoStartEnabled,
        scheduleAutoCompleteEnabled: form.scheduleAutoCompleteEnabled,
        scheduleWorkdayStartTime: form.scheduleWorkdayStartTime,
        scheduleWorkdayEndTime: form.scheduleWorkdayEndTime,

        approvalLinkExpiryDays: form.approvalLinkExpiryDays,
        defaultTaxRate: form.defaultTaxRate,
        twoFactorEnabled: form.twoFactorEnabled,
        ...(starterBrandingLocked
          ? {}
          : {
              companySealSignatureDataUrl: sealSignatureDataUrl ?? undefined,
              removeCompanySealSignature: removeSealSignature || undefined,
              pdfBrandTagline: form.pdfBrandTagline.trim() || undefined,
              pdfBrandColorHex: form.pdfBrandColorHex.trim().toUpperCase(),
            }),
      });

      const nextForm: FormState = {
        ...form,
        companyLogoUrl: updated.companyLogoUrl ?? "",
        companySealSignatureUrl: updated.companySealSignatureUrl ?? "",
        pdfBrandTagline: updated.pdfBrandTagline ?? "",
        pdfBrandColorHex: updated.pdfBrandColorHex ?? "#F59E0B",
      };
      setForm(nextForm);
      initialSnapshotRef.current = JSON.stringify(nextForm);
      setLogoDataUrl(null);
      setRemoveLogo(false);
      setSealSignatureDataUrl(null);
      setRemoveSealSignature(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["company-me"] }),
        queryClient.invalidateQueries({ queryKey: ["company-settings"] }),
      ]);
      await refreshMe();
      toast({ title: t("settingsPage.saved") });
    } catch (err) {
      toast({
        title: t("settingsPage.saveFailed"),
        description:
          err instanceof Error ? err.message : t("common.requestFailed"),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const onLogoPicked = async (file: File | null) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast({
        title: t("settingsPage.validation.selectImage"),
        variant: "destructive",
      });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: t("settingsPage.validation.logoMax"),
        variant: "destructive",
      });
      return;
    }
    const dataUrl = await toDataUrl(file);
    setLogoDataUrl(dataUrl);
    setRemoveLogo(false);
    if (form) {
      setForm({ ...form, companyLogoUrl: dataUrl });
    }
  };

  const onSealSignaturePicked = async (file: File | null) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast({
        title: t("settingsPage.validation.selectImage"),
        variant: "destructive",
      });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: t("settingsPage.validation.logoMax"),
        variant: "destructive",
      });
      return;
    }
    const dataUrl = await toDataUrl(file);
    setSealSignatureDataUrl(dataUrl);
    setRemoveSealSignature(false);
    if (form) {
      setForm({ ...form, companySealSignatureUrl: dataUrl });
    }
  };

  const openPriceBookEditor = (item?: PriceBookItem | null) => {
    setPriceBookTarget(item ?? null);
    setPriceBookThumbnailDataUrl(null);
    setPriceBookRemoveThumbnail(false);
    setPriceBookForm(
      item
        ? {
            name: item.name,
            type: item.type,
            description: item.description ?? "",
            unit: item.unit ?? "",
            pricingMode: item.pricingMode,
            defaultPrice: String(item.defaultPrice ?? ""),
            thumbnailUrl: item.thumbnailUrl ?? "",
          }
        : EMPTY_PRICE_BOOK_FORM,
    );
    setPriceBookEditorOpen(true);
  };

  const onPriceBookThumbnailPicked = async (file: File | null) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast({
        title: t("settingsPage.validation.selectImage"),
        variant: "destructive",
      });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: t("settingsPage.validation.thumbnailMax"),
        variant: "destructive",
      });
      return;
    }
    const dataUrl = await toDataUrl(file);
    setPriceBookThumbnailDataUrl(dataUrl);
    setPriceBookRemoveThumbnail(false);
    setPriceBookForm((prev) => ({ ...prev, thumbnailUrl: dataUrl }));
  };

  const savePriceBookItem = async () => {
    if (readOnly) return;
    if (!priceBookForm.name.trim()) {
      toast({
        title: t("settingsPage.validation.itemNameRequired"),
        variant: "destructive",
      });
      return;
    }
    const defaultPrice = Number(priceBookForm.defaultPrice || 0);
    if (!Number.isFinite(defaultPrice) || defaultPrice < 0) {
      toast({
        title: t("settingsPage.validation.defaultPrice"),
        variant: "destructive",
      });
      return;
    }

    setPriceBookSaving(true);
    try {
      const payload = {
        name: priceBookForm.name.trim(),
        type: priceBookForm.type,
        description: priceBookForm.description.trim() || undefined,
        thumbnailDataUrl: priceBookThumbnailDataUrl ?? undefined,
        removeThumbnail: priceBookRemoveThumbnail || undefined,
        unit: priceBookForm.unit.trim() || undefined,
        pricingMode: priceBookForm.pricingMode,
        defaultPrice,
      };

      if (priceBookTarget) {
        await updatePriceBookItem(priceBookTarget.id, payload);
      } else {
        await createPriceBookItem(payload);
      }

      await invalidatePriceBookData(queryClient);
      setPriceBookEditorOpen(false);
      setPriceBookTarget(null);
      setPriceBookThumbnailDataUrl(null);
      setPriceBookRemoveThumbnail(false);
      setPriceBookForm(EMPTY_PRICE_BOOK_FORM);
      toast({
        title: priceBookTarget
          ? t("settingsPage.priceBook.updated")
          : t("settingsPage.priceBook.added"),
      });
    } catch (err) {
      toast({
        title: t("settingsPage.saveFailed"),
        description:
          err instanceof Error ? err.message : t("common.requestFailed"),
        variant: "destructive",
      });
    } finally {
      setPriceBookSaving(false);
    }
  };

  const archiveItem = async (item: PriceBookItem) => {
    if (readOnly) return;
    try {
      await archivePriceBookItem(item.id);
      await invalidatePriceBookData(queryClient);
      toast({ title: t("settingsPage.priceBook.archived") });
    } catch (err) {
      toast({
        title: t("settingsPage.priceBook.archiveFailed"),
        description:
          err instanceof Error ? err.message : t("common.requestFailed"),
        variant: "destructive",
      });
    }
  };

  const removeItem = async (item: PriceBookItem) => {
    if (readOnly) return;
    const confirmed = window.confirm(
      `Remove "${item.name}" permanently? This cannot be undone.`,
    );
    if (!confirmed) return;

    try {
      await removePriceBookItem(item.id);
      await invalidatePriceBookData(queryClient);
      toast({ title: t("settingsPage.priceBook.removed") });
    } catch (err) {
      toast({
        title: t("settingsPage.priceBook.removeFailed"),
        description:
          err instanceof Error ? err.message : t("common.requestFailed"),
        variant: "destructive",
      });
    }
  };

  const importDefaultItems = async () => {
    if (readOnly) return;
    setPriceBookImporting(true);
    try {
      const result = await importDefaultPriceBookItems();
      await invalidatePriceBookData(queryClient);
      toast({
        title: t("settingsPage.priceBook.defaultImported"),
        description: `${result.importedCount} added, ${result.skippedCount} skipped (${result.companyType}).`,
      });
    } catch (err) {
      toast({
        title: t("settingsPage.priceBook.importFailed"),
        description:
          err instanceof Error ? err.message : t("common.requestFailed"),
        variant: "destructive",
      });
    } finally {
      setPriceBookImporting(false);
    }
  };

  const openProjectLabelEditor = (label?: ProjectLabel | null) => {
    setProjectLabelTarget(label ?? null);
    setProjectLabelForm(
      label
        ? {
            name: label.name,
            color: label.color,
          }
        : EMPTY_PROJECT_LABEL_FORM,
    );
    setProjectLabelEditorOpen(true);
  };

  const saveProjectLabelItem = async () => {
    if (readOnly) return;
    if (!projectLabelForm.name.trim()) {
      toast({
        title: t("settingsPage.projectLabels.nameRequired"),
        variant: "destructive",
      });
      return;
    }

    setProjectLabelSaving(true);
    try {
      if (projectLabelTarget) {
        await updateProjectLabel(projectLabelTarget.id, {
          name: projectLabelForm.name.trim(),
          color: projectLabelForm.color,
        });
      } else {
        await createProjectLabel({
          name: projectLabelForm.name.trim(),
          color: projectLabelForm.color,
        });
      }

      await invalidateProjectLabelData(queryClient);
      setProjectLabelEditorOpen(false);
      setProjectLabelTarget(null);
      setProjectLabelForm(EMPTY_PROJECT_LABEL_FORM);
      toast({
        title: projectLabelTarget
          ? t("settingsPage.projectLabels.updated")
          : t("settingsPage.projectLabels.created"),
      });
    } catch (err) {
      toast({
        title: t("settingsPage.saveFailed"),
        description:
          err instanceof Error ? err.message : t("common.requestFailed"),
        variant: "destructive",
      });
    } finally {
      setProjectLabelSaving(false);
    }
  };

  const confirmDeleteProjectLabel = async () => {
    if (readOnly || !projectLabelDeleteTarget) return;
    try {
      await deleteProjectLabel(projectLabelDeleteTarget.id);
      await invalidateProjectLabelData(queryClient);
      setProjectLabelDeleteTarget(null);
      toast({ title: t("settingsPage.projectLabels.deleted") });
    } catch (err) {
      toast({
        title: t("settingsPage.deleteFailed"),
        description:
          err instanceof Error ? err.message : t("common.requestFailed"),
        variant: "destructive",
      });
    }
  };

  const openEmailTemplateEditor = (template?: ApiTemplate | null) => {
    if (starterEmailTemplatesLocked) {
      showEmailTemplateUpgradeMessage();
      return;
    }
    if (template) {
      setEmailTemplateDraft({
        id: template.id,
        category: template.category,
        subType: template.subType,
        name: template.name,
        isDefault: template.isDefault,
        dataJson: { ...(template.dataJson ?? {}) },
      });
    } else {
      const subtype = emailTemplateSubType;
      const subtypeLabel =
        EMAIL_TEMPLATE_SUBTYPES.find((item) => item.value === subtype)?.label ??
        "Email";
      setEmailTemplateDraft({
        category: "email",
        subType: subtype,
        name: `${subtypeLabel} Template`,
        isDefault: false,
        dataJson: createDefaultEmailTemplateData(subtype),
      });
    }
    setEmailFocus("body");
    setEmailTemplateEditorOpen(true);
  };

  const updateEmailDraftData = (patch: Record<string, unknown>) => {
    if (starterEmailTemplatesLocked) return;
    setEmailTemplateDraft((prev) =>
      prev
        ? {
            ...prev,
            dataJson: {
              ...prev.dataJson,
              ...patch,
            },
          }
        : prev,
    );
  };

  const insertEmailVariable = (variable: string) => {
    if (starterEmailTemplatesLocked) return;
    if (!emailTemplateDraft) return;
    if (emailFocus === "subject") {
      updateEmailDraftData({
        subject: `${String(emailTemplateDraft.dataJson.subject ?? "")}${variable}`,
      });
      return;
    }
    updateEmailDraftData({
      body: `${String(emailTemplateDraft.dataJson.body ?? "")}${variable}`,
    });
  };

  const saveEmailTemplate = async () => {
    if (starterEmailTemplatesLocked) {
      showEmailTemplateUpgradeMessage();
      return;
    }
    if (!emailTemplateDraft) return;
    if (!emailTemplateDraft.name.trim()) {
      toast({
        title: t("settingsPage.emailTemplates.nameRequired"),
        variant: "destructive",
      });
      return;
    }
    setEmailTemplateSaving(true);
    try {
      if (emailTemplateDraft.id) {
        await updateTemplate(emailTemplateDraft.id, {
          name: emailTemplateDraft.name.trim(),
          dataJson: emailTemplateDraft.dataJson,
          isDefault: emailTemplateDraft.isDefault,
        });
      } else {
        await createTemplate({
          category: "email",
          subType: emailTemplateDraft.subType,
          name: emailTemplateDraft.name.trim(),
          dataJson: emailTemplateDraft.dataJson,
          isDefault: emailTemplateDraft.isDefault,
        });
      }
      await queryClient.invalidateQueries({ queryKey: ["templates"] });
      setEmailTemplateEditorOpen(false);
      setEmailTemplateDraft(null);
      toast({ title: t("settingsPage.emailTemplates.saved") });
    } catch (err) {
      toast({
        title: t("settingsPage.saveFailed"),
        description:
          err instanceof Error ? err.message : t("common.requestFailed"),
        variant: "destructive",
      });
    } finally {
      setEmailTemplateSaving(false);
    }
  };

  const runEmailTemplateAction = async (
    template: ApiTemplate,
    action: "setDefault" | "delete",
  ) => {
    if (starterEmailTemplatesLocked) {
      showEmailTemplateUpgradeMessage();
      return;
    }
    const actionKey = `${template.id}:${action}`;
    setEmailTemplateAction(actionKey);
    try {
      if (action === "setDefault") {
        await setTemplateDefault(template.id);
        toast({ title: t("settingsPage.emailTemplates.defaultUpdated") });
      }
      if (action === "delete") {
        if (template.isDefault) {
          toast({
            title: t("settingsPage.emailTemplates.cannotDeleteDefault"),
            description: t("settingsPage.emailTemplates.setAnotherDefault"),
            variant: "destructive",
          });
          return;
        }
        await deleteTemplate(template.id);
        toast({ title: t("settingsPage.emailTemplates.deleted") });
      }
      await queryClient.invalidateQueries({ queryKey: ["templates"] });
    } catch (err) {
      toast({
        title: t("settingsPage.actionFailed"),
        description:
          err instanceof Error ? err.message : t("common.requestFailed"),
        variant: "destructive",
      });
    } finally {
      setEmailTemplateAction(null);
    }
  };

  if (loading || !form || !settings) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <PageHeader
            title={t("pages.settings.title")}
            description={t("pages.settings.description")}
          />
          <RefreshingIndicator visible={isRefreshing} />
        </div>
        <SettingsLoading />
      </div>
    );
  }

  const isLaborPriceBook = priceBookForm.type === "LABOR";
  const isMaterialPriceBook = priceBookForm.type === "MATERIAL";
  const isEquipmentPriceBook = priceBookForm.type === "EQUIPMENT";
  const isOtherPriceBook = priceBookForm.type === "OTHER";

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageHeader
          title={t("pages.settings.title")}
          description={t("pages.settings.description")}
        />
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex items-center gap-2 rounded-md border bg-card px-3 py-2">
            <span className="text-sm font-medium text-primary">
              {t("settings.languageTitle")}
            </span>
            <div className="shrink-0">
              <LanguageSwitcher />
            </div>
          </div>
          <RefreshingIndicator visible={isRefreshing} />
        </div>
      </div>
      {readOnly && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Read-only access. Only Admin can edit workspace settings.
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-6">
        <div className="md:w-56 shrink-0">
          <nav className="flex md:flex-col gap-1 overflow-x-auto md:overflow-visible scrollbar-thin">
            {translatedSettingsTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-secondary"
                }`}
              >
                <tab.icon className="h-4 w-4 shrink-0" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="flex-1 rounded-lg border bg-card p-6 space-y-6 [&_h2]:text-[#0C1F4F] [&_h3]:text-[#0C1F4F] [&_label]:text-[#0C1F4F] [&_h2+p]:text-[#0C1F4F] [&_h3+p]:text-[#0C1F4F]">
          {activeTab === "company" && (
            <div className="space-y-5">
              <h2 className="font-semibold text-foreground text-lg">
                {t("settings.tabs.company")}
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>{t("settingsPage.company.companyName")}</Label>
                  <Input
                    value={form.companyName}
                    onChange={(e) => setField("companyName", e.target.value)}
                    disabled={readOnly}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("settingsPage.company.yourName")}</Label>
                  <Input
                    value={form.ownerFullName}
                    onChange={(e) => setField("ownerFullName", e.target.value)}
                    disabled={readOnly}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("settingsPage.company.companyType")}</Label>
                  <select
                    className="w-full h-9 rounded-md border bg-background px-3 text-sm disabled:opacity-70 disabled:cursor-not-allowed"
                    value={form.companyType}
                    onChange={(e) =>
                      setField(
                        "companyType",
                        e.target.value as FormState["companyType"],
                      )
                    }
                    disabled={readOnly}
                  >
                    {COMPANY_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {getCompanyTypeLabel(t, option.value)}
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] text-amber-700">
                    {t("settingsPage.company.companyTypeHelp")}
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label>{t("common.email")}</Label>
                  <Input
                    type="email"
                    value={form.companyEmail}
                    onChange={(e) => setField("companyEmail", e.target.value)}
                    disabled={readOnly}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("common.phone")}</Label>
                  <Input
                    value={form.companyPhone}
                    onChange={(e) => setField("companyPhone", e.target.value)}
                    disabled={readOnly}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("settingsPage.company.companyWebsite")}</Label>
                  <Input
                    type="url"
                    placeholder="https://example.com"
                    value={form.companyWebsite}
                    onChange={(e) => setField("companyWebsite", e.target.value)}
                    disabled={readOnly}
                  />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label>{t("settingsPage.company.address")}</Label>
                  <Input
                    value={form.companyAddress}
                    onChange={(e) => setField("companyAddress", e.target.value)}
                    disabled={readOnly}
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>{t("settingsPage.company.companyLogo")}</Label>
                <div className="flex items-center gap-4">
                  <div className="h-16 w-16 rounded-lg bg-muted flex items-center justify-center border overflow-hidden">
                    {form.companyLogoUrl ? (
                      <img
                        src={form.companyLogoUrl}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <Building2 className="h-6 w-6 text-muted-foreground" />
                    )}
                  </div>
                  <div className="space-y-1">
                    <label className="inline-flex">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) =>
                          void onLogoPicked(e.target.files?.[0] ?? null)
                        }
                        disabled={readOnly}
                      />
                      <span
                        className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-md border text-sm ${readOnly ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
                      >
                        <Upload className="h-3.5 w-3.5" />{" "}
                        {t("settingsPage.company.uploadLogo")}
                      </span>
                    </label>
                    <div>
                      <button
                        type="button"
                        className="text-xs text-muted-foreground hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={readOnly}
                        onClick={() => {
                          setRemoveLogo(true);
                          setLogoDataUrl(null);
                          setField("companyLogoUrl", "");
                        }}
                      >
                        {t("settingsPage.company.remove")}
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {t("settingsPage.company.logoHelp")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "branding" && (
            <div className="space-y-6">
              <div className="space-y-1">
                <h2 className="font-semibold text-foreground text-lg">
                  {t("settings.tabs.branding", { defaultValue: "Branding" })}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {t("settingsPage.branding.description", {
                    defaultValue:
                      "Customize how ticket PDFs look when customers preview, sign, and download them.",
                  })}
                </p>
              </div>

              {starterBrandingLocked ? (
                <Alert>
                  <Palette className="h-4 w-4" />
                  <AlertTitle>
                    {t("settingsPage.branding.lockedTitle", {
                      defaultValue: "Branding is locked on Starter",
                    })}
                  </AlertTitle>
                  <AlertDescription className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <span>
                      {t("settingsPage.branding.lockedDescription", {
                        defaultValue:
                          "Upgrade to Pro or Team to customize ticket PDF branding, colors, tagline, and company seal.",
                      })}
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full sm:w-auto"
                      onClick={() => navigate("/upgrade-plan")}
                    >
                      {t("settingsPage.branding.lockedAction", {
                        defaultValue: "Upgrade plan",
                      })}
                    </Button>
                  </AlertDescription>
                </Alert>
              ) : null}

              <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
                <div className="space-y-5">
                  <div className="space-y-2">
                    <Label>
                      {t("settingsPage.branding.pdfBrandColor", {
                        defaultValue: "PDF brand color",
                      })}
                    </Label>
                    <div className="flex flex-wrap items-center gap-3">
                      <Input
                        type="color"
                        value={form.pdfBrandColorHex}
                        onChange={(e) =>
                          setField(
                            "pdfBrandColorHex",
                            e.target.value.toUpperCase(),
                          )
                        }
                        disabled={brandingReadOnly}
                        className="h-11 w-16 cursor-pointer p-1"
                      />
                      <Input
                        value={form.pdfBrandColorHex}
                        onChange={(e) =>
                          setField(
                            "pdfBrandColorHex",
                            e.target.value.toUpperCase(),
                          )
                        }
                        disabled={brandingReadOnly}
                        className="w-[160px] font-mono uppercase"
                        placeholder="#F59E0B"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {t("settingsPage.branding.pdfBrandColorHelp", {
                        defaultValue:
                          "Used for ticket PDF accents, section headings, and summary emphasis.",
                      })}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <Label>
                        {t("settingsPage.branding.tagline", {
                          defaultValue: "Tagline",
                        })}
                      </Label>
                      <span className="text-xs text-muted-foreground">
                        {form.pdfBrandTagline.trim().length}/80
                      </span>
                    </div>
                    <Input
                      value={form.pdfBrandTagline}
                      onChange={(e) =>
                        setField("pdfBrandTagline", e.target.value.slice(0, 80))
                      }
                      disabled={brandingReadOnly}
                      maxLength={80}
                      placeholder={t(
                        "settingsPage.branding.taglinePlaceholder",
                        {
                          defaultValue: "Craftsmanship you can trust",
                        },
                      )}
                    />
                    <p className="text-xs text-muted-foreground">
                      {t("settingsPage.branding.taglineHelp", {
                        defaultValue:
                          "Shown under your company name in customer-facing ticket PDFs. Keep it short and brand-safe.",
                      })}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>
                      {t("settingsPage.branding.sealSignature", {
                        defaultValue: "Company seal / signature",
                      })}
                    </Label>
                    <div className="flex items-center gap-4">
                      <div className="h-24 w-24 rounded-lg bg-muted flex items-center justify-center border overflow-hidden">
                        {form.companySealSignatureUrl ? (
                          <img
                            src={form.companySealSignatureUrl}
                            className="h-full w-full object-contain"
                          />
                        ) : (
                          <Building2 className="h-8 w-8 text-muted-foreground" />
                        )}
                      </div>
                      <div className="space-y-2">
                        <label className="inline-flex">
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) =>
                              void onSealSignaturePicked(
                                e.target.files?.[0] ?? null,
                              )
                            }
                            disabled={brandingReadOnly}
                          />
                          <span
                            className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-md border text-sm ${
                              brandingReadOnly
                                ? "cursor-not-allowed opacity-60"
                                : "cursor-pointer"
                            }`}
                          >
                            <Upload className="h-3.5 w-3.5" />
                            {t("settingsPage.branding.uploadSealSignature", {
                              defaultValue: "Upload seal / signature",
                            })}
                          </span>
                        </label>
                        <div>
                          <button
                            type="button"
                            className="text-xs text-muted-foreground hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={brandingReadOnly}
                            onClick={() => {
                              setRemoveSealSignature(true);
                              setSealSignatureDataUrl(null);
                              setField("companySealSignatureUrl", "");
                            }}
                          >
                            {t("settingsPage.company.remove")}
                          </button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {t("settingsPage.branding.sealSignatureHelp", {
                            defaultValue:
                              "PNG/SVG/JPG, max 2MB. This image appears on customer-facing ticket PDFs together with the customer signature.",
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border bg-muted/20 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                    {t("settingsPage.branding.previewLabel", {
                      defaultValue: "Preview",
                    })}
                  </p>
                  <div className="mt-3 space-y-3 rounded-lg border bg-background p-4">
                    <div className="flex items-start justify-between gap-4 border-b pb-3">
                      <div className="flex items-center gap-3">
                        <div
                          className="h-10 w-10 rounded-lg border"
                          style={{ backgroundColor: form.pdfBrandColorHex }}
                        />
                        <div className="space-y-1">
                          <div className="text-sm font-semibold text-foreground">
                            {form.companyName || "Your company"}
                          </div>
                          {form.pdfBrandTagline.trim() ? (
                            <div className="text-xs font-medium text-muted-foreground">
                              {form.pdfBrandTagline.trim()}
                            </div>
                          ) : null}
                          <div className="text-xs text-muted-foreground">
                            {t("settingsPage.branding.previewSubcopy", {
                              defaultValue:
                                "Ticket PDFs will use your selected brand color and company mark.",
                            })}
                          </div>
                        </div>
                      </div>
                      <div
                        className="rounded-md px-3 py-2 text-xs font-semibold text-white"
                        style={{ backgroundColor: form.pdfBrandColorHex }}
                      >
                        {t("settingsPage.branding.previewTag", {
                          defaultValue: "Branded PDF",
                        })}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="rounded-lg border p-3">
                        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                          {t("settingsPage.branding.customerSignature", {
                            defaultValue: "Customer signature",
                          })}
                        </p>
                        <div className="mt-3 h-14 rounded-md border border-dashed border-muted-foreground/40" />
                      </div>
                      <div className="rounded-lg border p-3">
                        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                          {t("settingsPage.branding.companyMark", {
                            defaultValue: "Company mark",
                          })}
                        </p>
                        <div className="mt-3 flex h-14 items-center justify-center rounded-md border bg-muted/30">
                          {form.companySealSignatureUrl ? (
                            <img
                              src={form.companySealSignatureUrl}
                              className="max-h-12 max-w-full object-contain"
                            />
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              {t("settingsPage.branding.noSealYet", {
                                defaultValue: "No seal uploaded yet",
                              })}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "numbering" && (
            <div className="space-y-5">
              <h2 className="font-semibold text-foreground text-lg">
                Ticket Types & Numbering
              </h2>
              <p className="text-xs text-muted-foreground">
                Drag sections to control the tab order shown in New Ticket.
              </p>
              {form.ticketTypeOrder.map((key, index) => {
                const t =
                  key === "co"
                    ? {
                        key: "co" as const,
                        type: "Change Order",
                        enabled: form.changeOrderEnabled,
                        prefix: form.coPrefix,
                        format: form.coNumberFormat,
                        next: form.coNextNumber,
                      }
                    : key === "tm"
                      ? {
                          key: "tm" as const,
                          type: "Estimate",
                          enabled: form.tmTicketEnabled,
                          prefix: form.tmPrefix,
                          format: form.tmNumberFormat,
                          next: form.tmNextNumber,
                        }
                      : {
                          key: "ew" as const,
                          type: "Extra Work",
                          enabled: form.extraWorkEnabled,
                          prefix: form.extraWorkPrefix,
                          format: form.ewNumberFormat,
                          next: form.ewNextNumber,
                        };

                return (
                  <div
                    key={t.type}
                    className="rounded-lg border p-4 space-y-3"
                    draggable={!readOnly}
                    onDragStart={(event) => {
                      event.dataTransfer.setData("text/plain", String(index));
                      event.dataTransfer.effectAllowed = "move";
                    }}
                    onDragOver={(event) => {
                      if (readOnly) return;
                      event.preventDefault();
                      event.dataTransfer.dropEffect = "move";
                    }}
                    onDrop={(event) => {
                      if (readOnly) return;
                      event.preventDefault();
                      const fromIndex = Number(
                        event.dataTransfer.getData("text/plain"),
                      );
                      if (Number.isNaN(fromIndex) || fromIndex === index)
                        return;
                      moveTicketType(fromIndex, index);
                    }}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <span
                          className={`inline-flex h-8 w-8 items-center justify-center rounded-md border ${
                            readOnly
                              ? "text-muted-foreground"
                              : "cursor-grab text-muted-foreground"
                          }`}
                          title={
                            readOnly
                              ? "Only Admin can reorder"
                              : "Drag to reorder"
                          }
                        >
                          <GripVertical className="h-4 w-4" />
                        </span>
                        <h3 className="font-medium text-foreground">
                          {t.type}
                        </h3>
                      </div>
                      <Switch
                        checked={t.enabled}
                        disabled={readOnly}
                        onCheckedChange={(checked) => {
                          if (t.key === "co")
                            setField("changeOrderEnabled", checked);
                          if (t.key === "tm")
                            setField("tmTicketEnabled", checked);
                          if (t.key === "ew")
                            setField("extraWorkEnabled", checked);
                        }}
                      />
                    </div>
                    <div className="grid sm:grid-cols-3 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs">Prefix</Label>
                        <Input
                          value={t.prefix}
                          className="h-8"
                          disabled={readOnly}
                          onChange={(e) => {
                            if (t.key === "co")
                              setField("coPrefix", e.target.value);
                            if (t.key === "tm")
                              setField("tmPrefix", e.target.value);
                            if (t.key === "ew")
                              setField("extraWorkPrefix", e.target.value);
                          }}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Next Number</Label>
                        <Input
                          type="number"
                          min={1}
                          value={t.next}
                          className="h-8"
                          disabled={readOnly}
                          onChange={(e) => {
                            const value = Number(e.target.value || 1);
                            if (t.key === "co") setField("coNextNumber", value);
                            if (t.key === "tm") setField("tmNextNumber", value);
                            if (t.key === "ew") setField("ewNextNumber", value);
                          }}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Format</Label>
                        <select
                          className="w-full h-8 rounded-md border bg-background px-2 text-sm disabled:opacity-70 disabled:cursor-not-allowed"
                          value={t.format}
                          disabled={readOnly}
                          onChange={(e) => {
                            const value = e.target.value as
                              | "PREFIX-YEAR-SEQ"
                              | "PREFIX-SEQ";
                            if (t.key === "co")
                              setField("coNumberFormat", value);
                            if (t.key === "tm")
                              setField("tmNumberFormat", value);
                            if (t.key === "ew")
                              setField("ewNumberFormat", value);
                          }}
                        >
                          <option value="PREFIX-YEAR-SEQ">
                            PREFIX-YEAR-SEQ
                          </option>
                          <option value="PREFIX-SEQ">PREFIX-SEQ</option>
                        </select>
                      </div>
                    </div>
                    <Collapsible
                      open={ticketDefaultsOpen[t.key]}
                      onOpenChange={(open) =>
                        setTicketDefaultsOpen((prev) => ({
                          ...prev,
                          [t.key]: open,
                        }))
                      }
                    >
                      <div className="flex justify-end">
                        <CollapsibleTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-xs font-medium"
                          >
                            Ticket Defaults
                            <ChevronDown
                              className={`h-3.5 w-3.5 transition-transform ${
                                ticketDefaultsOpen[t.key] ? "rotate-180" : ""
                              }`}
                            />
                          </button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent forceMount className="pt-3">
                        <div
                          className="overflow-hidden transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]"
                          style={{
                            maxHeight: ticketDefaultsOpen[t.key] ? 1600 : 0,
                            opacity: ticketDefaultsOpen[t.key] ? 1 : 0,
                            transform: ticketDefaultsOpen[t.key]
                              ? "translateY(0)"
                              : "translateY(-14px)",
                          }}
                        >
                          <div className="rounded-md border bg-muted/20 p-3 space-y-4 [&_label]:text-[#0C1F4F]">
                            <p className="text-[11px] text-muted-foreground">
                              Configure default values used when creating new{" "}
                              <span className="font-medium text-foreground">
                                {t.type}
                              </span>{" "}
                              tickets.
                            </p>
                            <div className="grid sm:grid-cols-2 gap-3">
                              <div className="space-y-1.5">
                                <Label className="text-xs">Default Title</Label>
                                <Input
                                  className="h-8"
                                  disabled={readOnly || ticketDefaultsLoading}
                                  value={
                                    ticketDefaultsByType[t.key].defaultTitle
                                  }
                                  onChange={(e) =>
                                    setTicketDefaultsField(
                                      t.key,
                                      "defaultTitle",
                                      e.target.value,
                                    )
                                  }
                                />
                              </div>
                              <div className="space-y-1.5">
                                <Label className="text-xs">
                                  Default reminder cadence (days)
                                </Label>
                                <Input
                                  type="number"
                                  min={1}
                                  max={30}
                                  className="h-8"
                                  disabled={readOnly || ticketDefaultsLoading}
                                  value={
                                    ticketDefaultsByType[t.key]
                                      .defaultReminderCadenceDays
                                  }
                                  onChange={(e) =>
                                    setTicketDefaultsField(
                                      t.key,
                                      "defaultReminderCadenceDays",
                                      e.target.value,
                                    )
                                  }
                                />
                              </div>
                              <div className="space-y-1.5 sm:col-span-2">
                                <Label className="text-xs">
                                  Default Description
                                </Label>
                                <Textarea
                                  rows={4}
                                  disabled={readOnly || ticketDefaultsLoading}
                                  value={
                                    ticketDefaultsByType[t.key]
                                      .defaultDescription
                                  }
                                  onChange={(e) =>
                                    setTicketDefaultsField(
                                      t.key,
                                      "defaultDescription",
                                      e.target.value,
                                    )
                                  }
                                />
                              </div>
                            </div>
                            <div className="space-y-3">
                              <Label className="text-xs">
                                Line item defaults by category
                              </Label>
                              {(() => {
                                const categoryDefaults =
                                  ticketDefaultsByType[t.key]
                                    .lineItemDefaultsByCategory;
                                return (
                                  <div className="grid gap-3">
                                    <div className="rounded-md border p-3 space-y-2">
                                      <div className="flex flex-wrap items-end gap-3">
                                        <div
                                          className="h-9 shrink-0 rounded-md border bg-background px-3 text-sm font-medium flex items-center"
                                          style={{
                                            width: 220,
                                            minWidth: 220,
                                            flex: "0 0 220px",
                                          }}
                                        >
                                          Service / Labor
                                        </div>
                                        <div className="flex flex-wrap items-center gap-2.5 min-h-9">
                                          <Label className="text-xs text-muted-foreground">
                                            Pricing method
                                          </Label>
                                          <div className="inline-flex rounded-md border border-accent bg-muted/20 p-1">
                                            <button
                                              type="button"
                                              className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                                                categoryDefaults.LABOR
                                                  .pricingMethod === "HOURLY"
                                                  ? "bg-primary text-primary-foreground"
                                                  : "text-muted-foreground hover:bg-muted"
                                              }`}
                                              onClick={() =>
                                                setTicketCategoryDefault(
                                                  t.key,
                                                  "LABOR",
                                                  {
                                                    pricingMethod: "HOURLY",
                                                    unit:
                                                      categoryDefaults.LABOR
                                                        .unit || "hrs",
                                                  },
                                                )
                                              }
                                              disabled={
                                                readOnly ||
                                                ticketDefaultsLoading
                                              }
                                            >
                                              Hourly
                                            </button>
                                            <button
                                              type="button"
                                              className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                                                categoryDefaults.LABOR
                                                  .pricingMethod === "FIXED"
                                                  ? "bg-primary text-primary-foreground"
                                                  : "text-muted-foreground hover:bg-muted"
                                              }`}
                                              onClick={() =>
                                                setTicketCategoryDefault(
                                                  t.key,
                                                  "LABOR",
                                                  {
                                                    pricingMethod: "FIXED",
                                                  },
                                                )
                                              }
                                              disabled={
                                                readOnly ||
                                                ticketDefaultsLoading
                                              }
                                            >
                                              Fixed price
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                      <div
                                        className={`grid gap-2 items-end ${categoryDefaults.LABOR.pricingMethod === "FIXED" ? "sm:grid-cols-[1.5fr_160px_120px]" : "sm:grid-cols-[1.5fr_110px_130px_120px]"}`}
                                      >
                                        <div className="space-y-1">
                                          <Label className="text-xs text-muted-foreground">
                                            Description
                                          </Label>
                                          <Input
                                            value={
                                              categoryDefaults.LABOR.description
                                            }
                                            onChange={(e) =>
                                              setTicketCategoryDefault(
                                                t.key,
                                                "LABOR",
                                                { description: e.target.value },
                                              )
                                            }
                                            disabled={
                                              readOnly || ticketDefaultsLoading
                                            }
                                          />
                                        </div>
                                        {categoryDefaults.LABOR
                                          .pricingMethod === "HOURLY" ? (
                                          <>
                                            <div className="space-y-1">
                                              <Label className="text-xs text-muted-foreground">
                                                Hours
                                              </Label>
                                              <div className="relative">
                                                <Input
                                                  value={
                                                    categoryDefaults.LABOR
                                                      .quantity
                                                  }
                                                  onChange={(e) =>
                                                    setTicketCategoryDefault(
                                                      t.key,
                                                      "LABOR",
                                                      {
                                                        quantity:
                                                          e.target.value,
                                                      },
                                                    )
                                                  }
                                                  type="number"
                                                  min={0}
                                                  className="pr-10"
                                                  disabled={
                                                    readOnly ||
                                                    ticketDefaultsLoading
                                                  }
                                                />
                                                <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-muted-foreground">
                                                  hrs
                                                </span>
                                              </div>
                                            </div>
                                            <div className="space-y-1">
                                              <Label className="text-xs text-muted-foreground">
                                                Rate ($/hr)
                                              </Label>
                                              <Input
                                                value={
                                                  categoryDefaults.LABOR
                                                    .unitCost
                                                }
                                                onChange={(e) =>
                                                  setTicketCategoryDefault(
                                                    t.key,
                                                    "LABOR",
                                                    {
                                                      unitCost: e.target.value,
                                                    },
                                                  )
                                                }
                                                type="number"
                                                min={0}
                                                disabled={
                                                  readOnly ||
                                                  ticketDefaultsLoading
                                                }
                                              />
                                            </div>
                                          </>
                                        ) : (
                                          <div className="space-y-1">
                                            <Label className="text-xs text-muted-foreground">
                                              Amount ($)
                                            </Label>
                                            <Input
                                              value={
                                                categoryDefaults.LABOR
                                                  .fixedAmount
                                              }
                                              onChange={(e) =>
                                                setTicketCategoryDefault(
                                                  t.key,
                                                  "LABOR",
                                                  {
                                                    fixedAmount: e.target.value,
                                                  },
                                                )
                                              }
                                              type="number"
                                              min={0}
                                              disabled={
                                                readOnly ||
                                                ticketDefaultsLoading
                                              }
                                            />
                                          </div>
                                        )}
                                        <div className="space-y-1">
                                          <Label className="text-xs text-muted-foreground">
                                            Total
                                          </Label>
                                          <Input
                                            value={calculateTicketLineItemTotal(
                                              categoryDefaults.LABOR,
                                            ).toFixed(2)}
                                            readOnly
                                            className="bg-muted/20"
                                          />
                                        </div>
                                      </div>
                                    </div>

                                    <div className="rounded-md border p-3 space-y-2">
                                      <div className="flex flex-wrap items-end gap-3">
                                        <div
                                          className="h-9 shrink-0 rounded-md border bg-background px-3 text-sm font-medium flex items-center"
                                          style={{
                                            width: 220,
                                            minWidth: 220,
                                            flex: "0 0 220px",
                                          }}
                                        >
                                          Materials
                                        </div>
                                        <div className="inline-flex rounded-md border border-accent bg-muted/20 p-1">
                                          <button
                                            type="button"
                                            className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                                              categoryDefaults.MATERIAL.useUnit
                                                ? "bg-primary text-primary-foreground"
                                                : "text-muted-foreground hover:bg-muted"
                                            }`}
                                            onClick={() =>
                                              setTicketCategoryDefault(
                                                t.key,
                                                "MATERIAL",
                                                { useUnit: true },
                                              )
                                            }
                                            disabled={
                                              readOnly || ticketDefaultsLoading
                                            }
                                          >
                                            Include unit
                                          </button>
                                          <button
                                            type="button"
                                            className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                                              !categoryDefaults.MATERIAL.useUnit
                                                ? "bg-primary text-primary-foreground"
                                                : "text-muted-foreground hover:bg-muted"
                                            }`}
                                            onClick={() =>
                                              setTicketCategoryDefault(
                                                t.key,
                                                "MATERIAL",
                                                { useUnit: false },
                                              )
                                            }
                                            disabled={
                                              readOnly || ticketDefaultsLoading
                                            }
                                          >
                                            Hide unit
                                          </button>
                                        </div>
                                      </div>
                                      <div
                                        className={`grid gap-2 items-end ${categoryDefaults.MATERIAL.useUnit ? "sm:grid-cols-[1.5fr_110px_100px_130px_120px]" : "sm:grid-cols-[1.5fr_110px_130px_120px]"}`}
                                      >
                                        <div className="space-y-1">
                                          <Label className="text-xs text-muted-foreground">
                                            Material / Description
                                          </Label>
                                          <Input
                                            value={
                                              categoryDefaults.MATERIAL
                                                .description
                                            }
                                            onChange={(e) =>
                                              setTicketCategoryDefault(
                                                t.key,
                                                "MATERIAL",
                                                { description: e.target.value },
                                              )
                                            }
                                            disabled={
                                              readOnly || ticketDefaultsLoading
                                            }
                                          />
                                        </div>
                                        <div className="space-y-1">
                                          <Label className="text-xs text-muted-foreground">
                                            Quantity
                                          </Label>
                                          <Input
                                            value={
                                              categoryDefaults.MATERIAL.quantity
                                            }
                                            onChange={(e) =>
                                              setTicketCategoryDefault(
                                                t.key,
                                                "MATERIAL",
                                                { quantity: e.target.value },
                                              )
                                            }
                                            type="number"
                                            min={0}
                                            disabled={
                                              readOnly || ticketDefaultsLoading
                                            }
                                          />
                                        </div>
                                        {categoryDefaults.MATERIAL.useUnit ? (
                                          <div className="space-y-1">
                                            <Label className="text-xs text-muted-foreground">
                                              Unit
                                            </Label>
                                            <select
                                              className="h-9 rounded-md border bg-background px-2 text-sm w-full"
                                              value={
                                                categoryDefaults.MATERIAL.unit
                                              }
                                              onChange={(e) =>
                                                setTicketCategoryDefault(
                                                  t.key,
                                                  "MATERIAL",
                                                  { unit: e.target.value },
                                                )
                                              }
                                              disabled={
                                                readOnly ||
                                                ticketDefaultsLoading
                                              }
                                            >
                                              {materialUnitOptions.map(
                                                (unit) => (
                                                  <option
                                                    key={unit}
                                                    value={unit}
                                                  >
                                                    {unit}
                                                  </option>
                                                ),
                                              )}
                                            </select>
                                          </div>
                                        ) : null}
                                        <div className="space-y-1">
                                          <Label className="text-xs text-muted-foreground">
                                            Unit price
                                          </Label>
                                          <Input
                                            value={
                                              categoryDefaults.MATERIAL.unitCost
                                            }
                                            onChange={(e) =>
                                              setTicketCategoryDefault(
                                                t.key,
                                                "MATERIAL",
                                                { unitCost: e.target.value },
                                              )
                                            }
                                            type="number"
                                            min={0}
                                            disabled={
                                              readOnly || ticketDefaultsLoading
                                            }
                                          />
                                        </div>
                                        <div className="space-y-1">
                                          <Label className="text-xs text-muted-foreground">
                                            Total
                                          </Label>
                                          <Input
                                            value={calculateTicketLineItemTotal(
                                              categoryDefaults.MATERIAL,
                                            ).toFixed(2)}
                                            readOnly
                                            className="bg-muted/20"
                                          />
                                        </div>
                                      </div>
                                    </div>

                                    <div className="rounded-md border p-3 space-y-2">
                                      <div
                                        className="h-9 shrink-0 rounded-md border bg-background px-3 text-sm font-medium flex items-center"
                                        style={{
                                          width: 220,
                                          minWidth: 220,
                                          flex: "0 0 220px",
                                        }}
                                      >
                                        Equipment
                                      </div>
                                      <div className="grid sm:grid-cols-[1.5fr_110px_100px_130px_120px] gap-2 items-end">
                                        <div className="space-y-1">
                                          <Label className="text-xs text-muted-foreground">
                                            Equipment / Description
                                          </Label>
                                          <Input
                                            value={
                                              categoryDefaults.EQUIPMENT
                                                .description
                                            }
                                            onChange={(e) =>
                                              setTicketCategoryDefault(
                                                t.key,
                                                "EQUIPMENT",
                                                { description: e.target.value },
                                              )
                                            }
                                            disabled={
                                              readOnly || ticketDefaultsLoading
                                            }
                                          />
                                        </div>
                                        <div className="space-y-1">
                                          <Label className="text-xs text-muted-foreground">
                                            {categoryDefaults.EQUIPMENT.unit ===
                                              "hrs" ||
                                            categoryDefaults.EQUIPMENT.unit ===
                                              "days"
                                              ? "Duration"
                                              : "Quantity"}
                                          </Label>
                                          <Input
                                            value={
                                              categoryDefaults.EQUIPMENT
                                                .quantity
                                            }
                                            onChange={(e) =>
                                              setTicketCategoryDefault(
                                                t.key,
                                                "EQUIPMENT",
                                                { quantity: e.target.value },
                                              )
                                            }
                                            type="number"
                                            min={0}
                                            disabled={
                                              readOnly || ticketDefaultsLoading
                                            }
                                          />
                                        </div>
                                        <div className="space-y-1">
                                          <Label className="text-xs text-muted-foreground">
                                            Unit
                                          </Label>
                                          <select
                                            className="h-9 rounded-md border bg-background px-2 text-sm w-full"
                                            value={
                                              categoryDefaults.EQUIPMENT.unit
                                            }
                                            onChange={(e) =>
                                              setTicketCategoryDefault(
                                                t.key,
                                                "EQUIPMENT",
                                                { unit: e.target.value },
                                              )
                                            }
                                            disabled={
                                              readOnly || ticketDefaultsLoading
                                            }
                                          >
                                            {equipmentUnitOptions.map(
                                              (unit) => (
                                                <option key={unit} value={unit}>
                                                  {unit}
                                                </option>
                                              ),
                                            )}
                                          </select>
                                        </div>
                                        <div className="space-y-1">
                                          <Label className="text-xs text-muted-foreground">
                                            Rate
                                          </Label>
                                          <Input
                                            value={
                                              categoryDefaults.EQUIPMENT
                                                .unitCost
                                            }
                                            onChange={(e) =>
                                              setTicketCategoryDefault(
                                                t.key,
                                                "EQUIPMENT",
                                                { unitCost: e.target.value },
                                              )
                                            }
                                            type="number"
                                            min={0}
                                            disabled={
                                              readOnly || ticketDefaultsLoading
                                            }
                                          />
                                        </div>
                                        <div className="space-y-1">
                                          <Label className="text-xs text-muted-foreground">
                                            Total
                                          </Label>
                                          <Input
                                            value={calculateTicketLineItemTotal(
                                              categoryDefaults.EQUIPMENT,
                                            ).toFixed(2)}
                                            readOnly
                                            className="bg-muted/20"
                                          />
                                        </div>
                                      </div>
                                    </div>

                                    <div className="rounded-md border p-3 space-y-2">
                                      <div
                                        className="h-9 shrink-0 rounded-md border bg-background px-3 text-sm font-medium flex items-center"
                                        style={{
                                          width: 220,
                                          minWidth: 220,
                                          flex: "0 0 220px",
                                        }}
                                      >
                                        Other
                                      </div>
                                      <div className="grid sm:grid-cols-[1.5fr_160px_120px] gap-2 items-end">
                                        <div className="space-y-1">
                                          <Label className="text-xs text-muted-foreground">
                                            Description
                                          </Label>
                                          <Input
                                            value={
                                              categoryDefaults.OTHER.description
                                            }
                                            onChange={(e) =>
                                              setTicketCategoryDefault(
                                                t.key,
                                                "OTHER",
                                                { description: e.target.value },
                                              )
                                            }
                                            disabled={
                                              readOnly || ticketDefaultsLoading
                                            }
                                          />
                                        </div>
                                        <div className="space-y-1">
                                          <Label className="text-xs text-muted-foreground">
                                            Amount
                                          </Label>
                                          <Input
                                            value={
                                              categoryDefaults.OTHER.unitCost
                                            }
                                            onChange={(e) =>
                                              setTicketCategoryDefault(
                                                t.key,
                                                "OTHER",
                                                {
                                                  unitCost: e.target.value,
                                                  quantity: "1",
                                                  unit: "ea",
                                                },
                                              )
                                            }
                                            type="number"
                                            min={0}
                                            disabled={
                                              readOnly || ticketDefaultsLoading
                                            }
                                          />
                                        </div>
                                        <div className="space-y-1">
                                          <Label className="text-xs text-muted-foreground">
                                            Total
                                          </Label>
                                          <Input
                                            value={Number(
                                              categoryDefaults.OTHER.unitCost ||
                                                0,
                                            ).toFixed(2)}
                                            readOnly
                                            className="bg-muted/20"
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                            <div className="flex items-center justify-end">
                              <Button
                                type="button"
                                size="sm"
                                disabled={
                                  readOnly ||
                                  ticketDefaultsLoading ||
                                  ticketDefaultsSavingKey === t.key
                                }
                                onClick={() => void saveTicketDefaults(t.key)}
                              >
                                {ticketDefaultsSavingKey === t.key ? (
                                  <>
                                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                    Saving...
                                  </>
                                ) : (
                                  "Save Ticket Defaults"
                                )}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  </div>
                );
              })}
            </div>
          )}

          {activeTab === "approvals" && (
            <div className="space-y-5">
              <h2 className="font-semibold text-foreground text-lg">
                {t("settingsPage.approvals.title")}
              </h2>
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between gap-3">
                    <Label>{t("settingsPage.approvals.autoReminder")}</Label>
                    <Switch
                      checked={form.autoRemindersEnabled}
                      disabled={readOnly}
                      onCheckedChange={(checked) =>
                        setField("autoRemindersEnabled", checked)
                      }
                    />
                  </div>
                  <Input
                    type="number"
                    min={1}
                    max={30}
                    value={form.defaultReminderCadenceDays}
                    onChange={(e) =>
                      setField(
                        "defaultReminderCadenceDays",
                        Number(e.target.value || 1),
                      )
                    }
                    disabled={readOnly || !form.autoRemindersEnabled}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t("settingsPage.approvals.autoReminderHelp")}
                  </p>
                  <p className="text-[11px] text-muted-foreground/80">
                    {t("settingsPage.approvals.autoReminderHelp2")}
                  </p>
                </div>
                <div className="flex items-center justify-between rounded-lg border p-3">
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {t("settingsPage.approvals.requireComment")}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t("settingsPage.approvals.requireCommentHelp")}
                    </p>
                  </div>
                  <Switch
                    checked={form.requireRejectionComment}
                    disabled={readOnly}
                    onCheckedChange={(checked) =>
                      setField("requireRejectionComment", checked)
                    }
                  />
                </div>
                <div className="flex items-center justify-between rounded-lg border p-3">
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {t("settingsPage.approvals.manualOverride")}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t("settingsPage.approvals.manualOverrideHelp")}
                    </p>
                  </div>
                  <Switch
                    checked={form.allowManualApprovalOverride}
                    disabled={readOnly}
                    onCheckedChange={(checked) =>
                      setField("allowManualApprovalOverride", checked)
                    }
                  />
                </div>
                <div className="flex items-center justify-between rounded-lg border p-3">
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {t("settingsPage.approvals.requireSignature")}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t("settingsPage.approvals.requireSignatureHelp")}
                    </p>
                  </div>
                  <Switch
                    checked={form.requireSignatureToApprove}
                    disabled={readOnly}
                    onCheckedChange={(checked) =>
                      setField("requireSignatureToApprove", checked)
                    }
                  />
                </div>
                <div className="space-y-1.5 rounded-lg border p-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {t("settingsPage.approvals.autoDelete")}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t("settingsPage.approvals.autoDeleteHelp")}
                      </p>
                    </div>
                    <Switch
                      checked={form.autoDeletePendingApprovalEnabled}
                      disabled={readOnly}
                      onCheckedChange={(checked) =>
                        setField("autoDeletePendingApprovalEnabled", checked)
                      }
                    />
                  </div>
                  <Input
                    type="number"
                    min={1}
                    max={365}
                    value={form.autoDeletePendingApprovalDays}
                    onChange={(e) =>
                      setField(
                        "autoDeletePendingApprovalDays",
                        Number(e.target.value || 1),
                      )
                    }
                    disabled={
                      readOnly || !form.autoDeletePendingApprovalEnabled
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    {t("settingsPage.approvals.default10Days")}
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === "scheduling" && (
            <div className="space-y-5">
              <h2 className="font-semibold text-foreground text-lg">
                {t("settingsPage.scheduling.title", "Scheduling automation")}
              </h2>
              <div className="space-y-3">
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">
                      {t(
                        "settingsPage.scheduling.workdayStart",
                        "Workday start",
                      )}
                    </label>
                    <Input
                      type="time"
                      value={form.scheduleWorkdayStartTime}
                      disabled={readOnly}
                      onChange={(event) =>
                        setField("scheduleWorkdayStartTime", event.target.value)
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">
                      {t("settingsPage.scheduling.workdayEnd", "Workday end")}
                    </label>
                    <Input
                      type="time"
                      value={form.scheduleWorkdayEndTime}
                      disabled={readOnly}
                      onChange={(event) =>
                        setField("scheduleWorkdayEndTime", event.target.value)
                      }
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between rounded-lg border p-3">
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {t(
                        "settingsPage.scheduling.autoStart",
                        "Auto-start schedule blocks",
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t(
                        "settingsPage.scheduling.autoStartHelp",
                        "When enabled, a scheduled project block automatically moves to In Progress when its start time arrives.",
                      )}
                    </p>
                  </div>
                  <Switch
                    checked={form.scheduleAutoStartEnabled}
                    disabled={readOnly}
                    onCheckedChange={(checked) =>
                      setField("scheduleAutoStartEnabled", checked)
                    }
                  />
                </div>

                <div className="flex items-center justify-between rounded-lg border p-3">
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {t(
                        "settingsPage.scheduling.autoComplete",
                        "Auto-complete schedule blocks",
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t(
                        "settingsPage.scheduling.autoCompleteHelp",
                        "When enabled, an active project block automatically moves to Done when its scheduled end time passes.",
                      )}
                    </p>
                  </div>
                  <Switch
                    checked={form.scheduleAutoCompleteEnabled}
                    disabled={readOnly}
                    onCheckedChange={(checked) =>
                      setField("scheduleAutoCompleteEnabled", checked)
                    }
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === "emailtemplates" && (
            <div className="space-y-5">
              <div className="flex items-center justify-between gap-3">
                <h2 className="font-semibold text-foreground text-lg">
                  {t("settings.tabs.emailtemplates")}
                </h2>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    className="bg-accent text-accent-foreground hover:bg-accent/90 gap-1.5"
                    onClick={() => openEmailTemplateEditor(null)}
                    disabled={readOnly || starterEmailTemplatesLocked}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    {t("settingsPage.emailTemplates.new")}
                  </Button>
                </div>
              </div>

              {starterEmailTemplatesLocked ? (
                <Alert className="border-amber-500/40 bg-amber-50 text-amber-950">
                  <Info className="h-4 w-4 text-amber-600" />
                  <AlertTitle>
                    {t("settingsPage.emailTemplates.lockedTitle")}
                  </AlertTitle>
                  <AlertDescription className="mt-2 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <span>
                      {t("settingsPage.emailTemplates.lockedDescription")}
                    </span>
                    <Button
                      size="sm"
                      className="w-full md:w-auto"
                      onClick={() =>
                        navigate(
                          "/upgrade-plan?targetPlan=pro&interval=MONTHLY",
                        )
                      }
                    >
                      {t("settingsPage.emailTemplates.lockedAction")}
                    </Button>
                  </AlertDescription>
                </Alert>
              ) : null}

              <div className="relative">
                <div
                  className={
                    starterEmailTemplatesLocked
                      ? "pointer-events-none select-none opacity-45 blur-[1px]"
                      : ""
                  }
                  aria-hidden={starterEmailTemplatesLocked}
                >
                  {emailTemplatesQuery.isLoading &&
                  !emailTemplatesQuery.data ? (
                    <div className="rounded-lg border p-4 text-sm text-muted-foreground">
                      {t("settingsPage.emailTemplates.loading")}
                    </div>
                  ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {emailTemplates.map((item) => (
                        <div
                          key={item.id}
                          className="rounded-lg border bg-card p-4 hover:shadow-sm transition-all"
                        >
                          <div className="flex items-start justify-between mb-1">
                            <h3 className="font-medium text-foreground text-sm">
                              {item.name}
                            </h3>
                            {item.isDefault ? (
                              <span className="flex items-center gap-0.5 text-[10px] text-accent font-medium">
                                <Star className="h-3 w-3 fill-accent" />{" "}
                                {t("settingsPage.emailTemplates.default")}
                              </span>
                            ) : null}
                          </div>
                          <p className="text-xs text-muted-foreground mb-3">
                            {getEmailSubtypeLabel(t, item.subType)}
                          </p>
                          <div className="flex gap-1.5 flex-wrap">
                            {!item.isDefault ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-xs h-7 gap-1 text-muted-foreground"
                                onClick={() =>
                                  void runEmailTemplateAction(
                                    item,
                                    "setDefault",
                                  )
                                }
                                disabled={
                                  readOnly ||
                                  starterEmailTemplatesLocked ||
                                  emailTemplateAction ===
                                    `${item.id}:setDefault`
                                }
                              >
                                <Star className="h-3 w-3" />{" "}
                                {t("settingsPage.emailTemplates.setDefault")}
                              </Button>
                            ) : null}
                            {!item.isDefault ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-xs h-7 gap-1 text-destructive hover:text-destructive"
                                onClick={() =>
                                  void runEmailTemplateAction(item, "delete")
                                }
                                disabled={
                                  readOnly ||
                                  starterEmailTemplatesLocked ||
                                  emailTemplateAction === `${item.id}:delete`
                                }
                              >
                                <Trash2 className="h-3 w-3" />{" "}
                                {t("common.delete")}
                              </Button>
                            ) : null}
                          </div>
                          <div className="mt-3 flex justify-end">
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs h-7 gap-1"
                              onClick={() => openEmailTemplateEditor(item)}
                              disabled={starterEmailTemplatesLocked}
                            >
                              <Pencil className="h-3 w-3" />{" "}
                              {t("settingsPage.edit")}
                            </Button>
                          </div>
                        </div>
                      ))}
                      {emailTemplates.length === 0 ? (
                        <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                          {t("settingsPage.emailTemplates.none")}
                        </div>
                      ) : null}
                    </div>
                  )}

                  <div className="rounded-lg border bg-muted/30 p-4 space-y-2">
                    <h4 className="text-sm font-medium text-foreground">
                      {t("settingsPage.emailTemplates.availableVariables")}
                    </h4>
                    <div className="flex flex-wrap gap-1.5">
                      {emailTemplateVariables.map((v) => (
                        <code
                          key={v}
                          className="text-xs bg-card border rounded px-2 py-1 text-foreground font-mono"
                        >
                          {v}
                        </code>
                      ))}
                    </div>
                  </div>
                </div>
                {starterEmailTemplatesLocked ? (
                  <div className="absolute inset-0 rounded-xl bg-background/35 shadow-[inset_0_0_0_1px_rgba(245,158,11,0.18)]" />
                ) : null}
              </div>
            </div>
          )}

          {activeTab === "notifications" && (
            <div className="space-y-5">
              <h2 className="font-semibold text-foreground text-lg">
                {t("settings.tabs.notifications")}
              </h2>
              {[
                [
                  t("settingsPage.notifications.ticketApproved"),
                  "notifyTicketApproved",
                ],
                [
                  t("settingsPage.notifications.ticketRejected"),
                  "notifyTicketRejected",
                ],
                [
                  t("settingsPage.notifications.signatureCaptured"),
                  "notifySignatureCaptured",
                ],
                [
                  t("settingsPage.notifications.reminderSent"),
                  "notifyReminderSent",
                ],
                [
                  t("settingsPage.notifications.paymentFollowupSent"),
                  "notifyPaymentFollowupSent",
                ],
                [
                  t("settingsPage.notifications.paymentRecorded"),
                  "notifyPaymentRecorded",
                ],
                [
                  t("settingsPage.notifications.weeklySummary"),
                  "notifyWeeklySummary",
                ],
              ].map(([label, key]) => (
                <div
                  key={String(key)}
                  className="flex items-center justify-between rounded-lg border p-3"
                >
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {label}
                    </p>
                  </div>
                  <Switch
                    checked={Boolean(form[key as keyof FormState])}
                    disabled={readOnly || key === "notifyWeeklySummary"}
                    onCheckedChange={(checked) =>
                      setField(key as keyof FormState, checked as never)
                    }
                  />
                </div>
              ))}
            </div>
          )}

          {activeTab === "security" && (
            <div className="space-y-5">
              <h2 className="font-semibold text-foreground text-lg">
                {t("settings.tabs.security")}
              </h2>
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {t("settingsPage.security.twoFactor")}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t("settingsPage.comingSoon")}
                  </p>
                </div>
                <Switch
                  checked={form.twoFactorEnabled}
                  disabled
                  onCheckedChange={(checked) =>
                    setField("twoFactorEnabled", checked)
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label>{t("settingsPage.security.approvalLinkExpiry")}</Label>
                <Input
                  type="number"
                  min={1}
                  max={60}
                  value={form.approvalLinkExpiryDays}
                  onChange={(e) =>
                    setField(
                      "approvalLinkExpiryDays",
                      Number(e.target.value || 1),
                    )
                  }
                  disabled={readOnly}
                />
              </div>
            </div>
          )}

          {activeTab === "billing" && (
            <div className="space-y-5">
              <h2 className="font-semibold text-foreground text-lg">
                {t("settings.tabs.billing")}
              </h2>
              <div className="space-y-1.5 max-w-sm">
                <Label>{t("settingsPage.billing.defaultTaxRate")}</Label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step="0.01"
                  value={form.defaultTaxRate}
                  onChange={(e) =>
                    setField("defaultTaxRate", Number(e.target.value || 0))
                  }
                  disabled={readOnly}
                  placeholder="8.25"
                />
                <p className="text-xs text-muted-foreground">
                  {t("settingsPage.billing.defaultTaxRateHelp")}
                </p>
              </div>
              {!readOnly ? (
                <div>
                  <Button
                    onClick={() => void saveChanges()}
                    disabled={saving}
                    className="bg-accent text-accent-foreground hover:bg-accent/90 gap-1.5"
                  >
                    {saving ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Save className="h-3.5 w-3.5" />
                    )}
                    {t("common.saveChanges")}
                  </Button>
                </div>
              ) : null}
            </div>
          )}

          {activeTab === "projectlabels" && (
            <div className="space-y-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="font-semibold text-foreground text-lg">
                      {t("settings.tabs.projectlabels")}
                    </h2>
                    <Popover
                      open={projectLabelsHelpOpen}
                      onOpenChange={setProjectLabelsHelpOpen}
                    >
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-transparent text-muted-foreground transition-colors hover:bg-muted/30 hover:text-[#0C1F4F]"
                          aria-label={t(
                            "settingsPage.projectLabels.help.ariaLabel",
                          )}
                        >
                          <Info className="h-3.5 w-3.5" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        align="start"
                        className="w-[360px] border-border/80 bg-white p-4 shadow-lg"
                      >
                        <div className="space-y-3">
                          <div>
                            <p className="text-sm font-semibold text-[#0C1F4F]">
                              {t("settingsPage.projectLabels.help.title")}
                            </p>
                            <p className="mt-1 text-sm leading-6 text-muted-foreground">
                              {t("settingsPage.projectLabels.help.subtitle")}
                            </p>
                          </div>
                          <ul className="space-y-2 text-sm leading-6 text-muted-foreground">
                            <li>
                              {t(
                                "settingsPage.projectLabels.help.items.workflow",
                              )}
                            </li>
                            <li>
                              {t(
                                "settingsPage.projectLabels.help.items.tracking",
                              )}
                            </li>
                            <li>
                              {t("settingsPage.projectLabels.help.items.color")}
                            </li>
                          </ul>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t("settingsPage.projectLabels.description")}
                  </p>
                </div>
                {!readOnly ? (
                  <Button
                    onClick={() => openProjectLabelEditor()}
                    className="bg-accent text-accent-foreground hover:bg-accent/90 gap-1.5"
                  >
                    <Tags className="h-4 w-4" />
                    {t("settingsPage.projectLabels.add")}
                  </Button>
                ) : null}
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="relative sm:max-w-sm sm:flex-1">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    className="pl-9"
                    value={projectLabelSearch}
                    onChange={(e) => setProjectLabelSearch(e.target.value)}
                    placeholder={t("settingsPage.projectLabels.search")}
                  />
                </div>
                <div className="sm:ml-auto">
                  <RefreshingIndicator
                    visible={
                      projectLabelsQuery.isFetching &&
                      Boolean(projectLabelsQuery.data)
                    }
                  />
                </div>
              </div>

              {projectLabelsQuery.isLoading && !projectLabelsQuery.data ? (
                <div className="space-y-2">
                  <Skeleton className="h-14 w-full" />
                  <Skeleton className="h-14 w-full" />
                  <Skeleton className="h-14 w-full" />
                </div>
              ) : (
                <div className="rounded-lg border divide-y">
                  {(projectLabelsQuery.data ?? []).filter((label) =>
                    label.name
                      .toLowerCase()
                      .includes(projectLabelSearch.trim().toLowerCase()),
                  ).length === 0 ? (
                    <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                      {t("settingsPage.projectLabels.none")}
                    </div>
                  ) : (
                    (projectLabelsQuery.data ?? [])
                      .filter((label) =>
                        label.name
                          .toLowerCase()
                          .includes(projectLabelSearch.trim().toLowerCase()),
                      )
                      .map((label) => (
                        <div
                          key={label.id}
                          className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <span
                              className="inline-flex h-3 w-3 shrink-0 rounded-full"
                              style={{ backgroundColor: label.color }}
                            />
                            <span
                              className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium"
                              style={{
                                color: label.color,
                                borderColor: `${label.color}40`,
                                backgroundColor: `${label.color}14`,
                              }}
                            >
                              {label.name}
                            </span>
                          </div>
                          {!readOnly ? (
                            <div className="flex items-center gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="gap-1.5"
                                onClick={() => openProjectLabelEditor(label)}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                                {t("settingsPage.edit")}
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="gap-1.5"
                                onClick={() =>
                                  setProjectLabelDeleteTarget(label)
                                }
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                                {t("common.delete")}
                              </Button>
                            </div>
                          ) : null}
                        </div>
                      ))
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === "pricebook" && (
            <div className="space-y-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="font-semibold text-foreground text-lg">
                    {t("settings.tabs.pricebook")}
                  </h2>
                  <p className="text-xs text-muted-foreground">
                    {t("settingsPage.priceBook.description")}
                  </p>
                </div>
                {!readOnly ? (
                  <div className="flex items-center gap-2 sm:justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => void importDefaultItems()}
                      disabled={priceBookImporting}
                      className="gap-1.5"
                    >
                      {priceBookImporting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4" />
                      )}
                      {t("settingsPage.priceBook.importDefaults")}
                    </Button>
                  </div>
                ) : null}
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:flex-1">
                  <div className="relative sm:max-w-sm sm:flex-1">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      className="pl-9"
                      value={priceBookSearch}
                      onChange={(e) => setPriceBookSearch(e.target.value)}
                      placeholder={t("settingsPage.priceBook.search")}
                    />
                  </div>
                  <label className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                    <input
                      type="checkbox"
                      checked={priceBookIncludeArchived}
                      onChange={(e) =>
                        setPriceBookIncludeArchived(e.target.checked)
                      }
                    />
                    {t("settingsPage.priceBook.showArchived")}
                  </label>
                </div>
                <div className="flex items-center gap-2 sm:ml-4 sm:shrink-0">
                  <RefreshingIndicator
                    visible={
                      priceBookQuery.isFetching && Boolean(priceBookQuery.data)
                    }
                  />
                  {!readOnly ? (
                    <Button
                      onClick={() => openPriceBookEditor()}
                      className="bg-[#F38C16] text-white hover:bg-[#DD7A12] gap-1.5"
                    >
                      <Package className="h-4 w-4" />
                      {t("settingsPage.priceBook.add")}
                    </Button>
                  ) : null}
                </div>
              </div>

              {priceBookQuery.isLoading && !priceBookQuery.data ? (
                <div className="space-y-2">
                  <Skeleton className="h-14 w-full" />
                  <Skeleton className="h-14 w-full" />
                  <Skeleton className="h-14 w-full" />
                </div>
              ) : (
                <div className="rounded-lg border divide-y">
                  {(priceBookQuery.data ?? []).length === 0 ? (
                    <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                      {t("settingsPage.priceBook.none")}
                    </div>
                  ) : (
                    (priceBookQuery.data ?? []).map((item) => (
                      <div
                        key={item.id}
                        className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="h-12 w-12 shrink-0 overflow-hidden rounded-md border bg-muted/30 flex items-center justify-center">
                            {item.thumbnailUrl ? (
                              <img
                                src={item.thumbnailUrl}
                                alt={item.name}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <Package className="h-5 w-5 text-muted-foreground" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-foreground">
                              {item.name}
                              {item.archivedAt ? (
                                <span className="ml-2 rounded-full border px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                                  {t("settingsPage.archived")}
                                </span>
                              ) : null}
                            </p>
                            <p className="truncate text-xs text-muted-foreground">
                              {getPriceBookTypeLabel(t, item.type)} •{" "}
                              {item.pricingMode === "NO_UNITS"
                                ? t("settingsPage.priceBook.flatPrice")
                                : t("settingsPage.priceBook.trackUnits")}
                              {item.unit ? ` • ${item.unit}` : ""}
                            </p>
                            {item.description ? (
                              <p className="truncate text-xs text-muted-foreground/90">
                                {item.description}
                              </p>
                            ) : null}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 sm:gap-3">
                          <div className="text-sm font-semibold text-foreground sm:min-w-24 sm:text-right">
                            ${Number(item.defaultPrice ?? 0).toFixed(2)}
                          </div>
                          {!readOnly ? (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button
                                  type="button"
                                  className="flex h-7 w-7 items-center justify-center rounded-full hover:bg-secondary/60"
                                >
                                  <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-44">
                                <DropdownMenuItem
                                  onClick={() => openPriceBookEditor(item)}
                                >
                                  <Pencil className="mr-2 h-3.5 w-3.5" />
                                  {t("settingsPage.edit")}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => void archiveItem(item)}
                                  disabled={Boolean(item.archivedAt)}
                                >
                                  <Archive className="mr-2 h-3.5 w-3.5" />
                                  {item.archivedAt
                                    ? t("settingsPage.archived")
                                    : t("settingsPage.archive")}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => void removeItem(item)}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <Trash2 className="mr-2 h-3.5 w-3.5" />
                                  {t("settingsPage.remove")}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          ) : null}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === "integrations" && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="rounded-full bg-muted p-4 mb-4">
                <Puzzle className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-1">
                {
                  translatedSettingsTabs.find((tab) => tab.id === activeTab)
                    ?.label
                }
              </h3>
              <p className="text-sm text-muted-foreground">
                {t("settingsPage.comingSoon")}
              </p>
            </div>
          )}
        </div>
      </div>

      <Dialog
        open={projectLabelEditorOpen}
        onOpenChange={setProjectLabelEditorOpen}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {projectLabelTarget
                ? t("settingsPage.projectLabels.editDialog")
                : t("settingsPage.projectLabels.addDialog")}
            </DialogTitle>
            <DialogDescription>
              {t("settingsPage.projectLabels.dialogDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>{t("settingsPage.nameRequired")}</Label>
              <Input
                value={projectLabelForm.name}
                onChange={(e) =>
                  setProjectLabelForm((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
                placeholder={t("settingsPage.projectLabels.placeholder")}
              />
            </div>
            <div className="space-y-2">
              <Label>{t("settingsPage.projectLabels.color")}</Label>
              <div className="flex flex-wrap gap-2">
                {PROJECT_LABEL_PALETTE.map((color) => {
                  const selected = projectLabelForm.color === color;
                  return (
                    <button
                      key={color}
                      type="button"
                      className={`inline-flex h-9 w-9 items-center justify-center rounded-full border transition-transform ${selected ? "scale-105 shadow-sm" : "hover:scale-105"}`}
                      style={{
                        borderColor: selected ? color : `${color}40`,
                        backgroundColor: `${color}18`,
                      }}
                      onClick={() =>
                        setProjectLabelForm((prev) => ({ ...prev, color }))
                      }
                    >
                      <span
                        className="inline-flex h-4 w-4 rounded-full"
                        style={{ backgroundColor: color }}
                      />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2.5">
            <Button
              variant="outline"
              onClick={() => setProjectLabelEditorOpen(false)}
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={() => void saveProjectLabelItem()}
              disabled={projectLabelSaving}
              className="bg-accent text-accent-foreground hover:bg-accent/90 gap-1.5"
            >
              {projectLabelSaving ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Save className="h-3.5 w-3.5" />
              )}
              {t("common.saveChanges")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={confirmCompanyTypeChangeOpen}
        onOpenChange={setConfirmCompanyTypeChangeOpen}
      >
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>
              {t("settingsPage.companyTypeWarning.title")}
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              {t("settingsPage.companyTypeWarning.description")}
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            {t("settingsPage.companyTypeWarning.notice")}
          </div>
          <DialogFooter className="gap-2.5">
            <Button
              variant="outline"
              onClick={() => setConfirmCompanyTypeChangeOpen(false)}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setConfirmCompanyTypeChangeOpen(false);
                void saveChanges(true);
              }}
            >
              {t("settingsPage.companyTypeWarning.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(projectLabelDeleteTarget)}
        onOpenChange={(open) => !open && setProjectLabelDeleteTarget(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t("settingsPage.projectLabels.deleteDialog")}
            </DialogTitle>
            <DialogDescription>
              {projectLabelDeleteTarget
                ? t("settingsPage.projectLabels.deleteDescription", {
                    name: projectLabelDeleteTarget.name,
                  })
                : t("settingsPage.projectLabels.deleteDescriptionFallback")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2.5">
            <Button
              variant="outline"
              onClick={() => setProjectLabelDeleteTarget(null)}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={() => void confirmDeleteProjectLabel()}
            >
              {t("common.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={priceBookEditorOpen} onOpenChange={setPriceBookEditorOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle
              className={!priceBookTarget ? "text-[#F38C16]" : undefined}
            >
              {priceBookTarget
                ? t("settingsPage.priceBook.editDialog")
                : t("settingsPage.priceBook.addDialog")}
            </DialogTitle>
            <DialogDescription>
              {t("settingsPage.priceBook.dialogDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>
                  <span>
                    {t("settingsPage.nameRequired").replace("*", "").trim()}
                  </span>
                  <span className="ml-1 text-[#F38C16]">*</span>
                </Label>
                <Input
                  value={priceBookForm.name}
                  onChange={(e) =>
                    setPriceBookForm((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  placeholder={t("settingsPage.priceBook.namePlaceholder")}
                />
              </div>
              <div className="space-y-1.5">
                <Label>
                  <span>
                    {t("settingsPage.typeRequired").replace("*", "").trim()}
                  </span>
                  <span className="ml-1 text-[#F38C16]">*</span>
                </Label>
                <select
                  className="w-full h-9 rounded-md border bg-background px-3 text-sm"
                  value={priceBookForm.type}
                  onChange={(e) =>
                    setPriceBookForm((prev) => ({
                      ...prev,
                      type: e.target.value as PriceBookItemType,
                    }))
                  }
                >
                  {PRICE_BOOK_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {getPriceBookTypeLabel(t, option.value)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>{t("settingsPage.description")}</Label>
              <Textarea
                rows={4}
                value={priceBookForm.description}
                onChange={(e) =>
                  setPriceBookForm((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder={t("settingsPage.priceBook.descriptionPlaceholder")}
              />
            </div>

            <div className="space-y-1.5">
              <Label>{t("settingsPage.priceBook.thumbnail")}</Label>
              <div className="flex items-center gap-3">
                <div className="h-14 w-14 rounded-md border bg-muted/30 overflow-hidden flex items-center justify-center">
                  {priceBookForm.thumbnailUrl ? (
                    <img
                      src={priceBookForm.thumbnailUrl}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <Package className="h-5 w-5 text-muted-foreground" />
                  )}
                </div>
                <div className="space-y-1">
                  <label className="inline-flex">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) =>
                        void onPriceBookThumbnailPicked(
                          e.target.files?.[0] ?? null,
                        )
                      }
                    />
                    <span className="inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-md border px-3 text-sm">
                      <Upload className="h-3.5 w-3.5" />
                      {t("settingsPage.upload")}
                    </span>
                  </label>
                  <button
                    type="button"
                    className="block text-xs text-muted-foreground hover:underline"
                    onClick={() => {
                      setPriceBookRemoveThumbnail(true);
                      setPriceBookThumbnailDataUrl(null);
                      setPriceBookForm((prev) => ({
                        ...prev,
                        thumbnailUrl: "",
                      }));
                    }}
                  >
                    {t("settingsPage.remove")}
                  </button>
                </div>
              </div>
            </div>

            {isLaborPriceBook ? (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="mr-[20px]">
                    {t("settingsPage.priceBook.pricingMethod")}
                  </Label>
                  <div className="inline-flex rounded-md border bg-muted/20 p-1">
                    <button
                      type="button"
                      className={`rounded px-3 py-1.5 text-xs font-medium transition-colors ${priceBookForm.pricingMode === "TRACK_UNITS" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
                      onClick={() =>
                        setPriceBookForm((prev) => ({
                          ...prev,
                          pricingMode: "TRACK_UNITS",
                          unit: prev.unit || "ea",
                        }))
                      }
                    >
                      {t("settingsPage.priceBook.hourlyRate")}
                    </button>
                    <button
                      type="button"
                      className={`rounded px-3 py-1.5 text-xs font-medium transition-colors ${priceBookForm.pricingMode === "NO_UNITS" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
                      onClick={() =>
                        setPriceBookForm((prev) => ({
                          ...prev,
                          pricingMode: "NO_UNITS",
                          unit: "",
                        }))
                      }
                    >
                      {t("settingsPage.priceBook.flatFee")}
                    </button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>
                    {priceBookForm.pricingMode === "TRACK_UNITS"
                      ? t("settingsPage.priceBook.defaultHourlyRate")
                      : t("settingsPage.priceBook.defaultAmount")}
                  </Label>
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    value={priceBookForm.defaultPrice}
                    onChange={(e) =>
                      setPriceBookForm((prev) => ({
                        ...prev,
                        defaultPrice: e.target.value,
                      }))
                    }
                    placeholder="0.00"
                  />
                </div>
              </div>
            ) : null}

            {isMaterialPriceBook ? (
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="mr-[20px]">
                    {t("settingsPage.priceBook.pricingMethod")}
                  </Label>
                  <div className="inline-flex rounded-md border bg-muted/20 p-1">
                    <button
                      type="button"
                      className={`rounded px-3 py-1.5 text-xs font-medium transition-colors ${priceBookForm.pricingMode === "TRACK_UNITS" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
                      onClick={() =>
                        setPriceBookForm((prev) => ({
                          ...prev,
                          pricingMode: "TRACK_UNITS",
                          unit: prev.unit || "ea",
                        }))
                      }
                    >
                      {t("settingsPage.priceBook.perUnit")}
                    </button>
                    <button
                      type="button"
                      className={`rounded px-3 py-1.5 text-xs font-medium transition-colors ${priceBookForm.pricingMode === "NO_UNITS" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
                      onClick={() =>
                        setPriceBookForm((prev) => ({
                          ...prev,
                          pricingMode: "NO_UNITS",
                          unit: "",
                        }))
                      }
                    >
                      {t("settingsPage.priceBook.flatCost")}
                    </button>
                  </div>
                </div>
                {priceBookForm.pricingMode === "TRACK_UNITS" ? (
                  <div className="space-y-1.5">
                    <Label>{t("settingsPage.unit")}</Label>
                    <select
                      className="w-full h-9 rounded-md border bg-background px-3 text-sm"
                      value={priceBookForm.unit || "ea"}
                      onChange={(e) =>
                        setPriceBookForm((prev) => ({
                          ...prev,
                          unit: e.target.value,
                        }))
                      }
                    >
                      {PRICE_BOOK_UNIT_OPTIONS.map((unit) => (
                        <option key={unit} value={unit}>
                          {unit}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}
                <div className="space-y-1.5">
                  <Label>
                    {priceBookForm.pricingMode === "TRACK_UNITS"
                      ? t("settingsPage.priceBook.defaultUnitPrice")
                      : t("settingsPage.priceBook.defaultAmountShort")}
                  </Label>
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    value={priceBookForm.defaultPrice}
                    onChange={(e) =>
                      setPriceBookForm((prev) => ({
                        ...prev,
                        defaultPrice: e.target.value,
                      }))
                    }
                    placeholder="0.00"
                  />
                </div>
              </div>
            ) : null}

            {isEquipmentPriceBook ? (
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="mr-[20px]">
                    {t("settingsPage.priceBook.pricingMethod")}
                  </Label>
                  <div className="inline-flex rounded-md border bg-muted/20 p-1">
                    <button
                      type="button"
                      className={`rounded px-3 py-1.5 text-xs font-medium transition-colors ${priceBookForm.pricingMode === "TRACK_UNITS" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
                      onClick={() =>
                        setPriceBookForm((prev) => ({
                          ...prev,
                          pricingMode: "TRACK_UNITS",
                          unit: prev.unit || "ea",
                        }))
                      }
                    >
                      {t("settingsPage.priceBook.rateBased")}
                    </button>
                    <button
                      type="button"
                      className={`rounded px-3 py-1.5 text-xs font-medium transition-colors ${priceBookForm.pricingMode === "NO_UNITS" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
                      onClick={() =>
                        setPriceBookForm((prev) => ({
                          ...prev,
                          pricingMode: "NO_UNITS",
                          unit: "",
                        }))
                      }
                    >
                      {t("settingsPage.priceBook.flatCost")}
                    </button>
                  </div>
                </div>
                {priceBookForm.pricingMode === "TRACK_UNITS" ? (
                  <div className="space-y-1.5">
                    <Label>{t("settingsPage.priceBook.billingUnit")}</Label>
                    <select
                      className="w-full h-9 rounded-md border bg-background px-3 text-sm"
                      value={priceBookForm.unit || "ea"}
                      onChange={(e) =>
                        setPriceBookForm((prev) => ({
                          ...prev,
                          unit: e.target.value,
                        }))
                      }
                    >
                      {PRICE_BOOK_UNIT_OPTIONS.map((unit) => (
                        <option key={unit} value={unit}>
                          {unit}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}
                <div className="space-y-1.5">
                  <Label>
                    {priceBookForm.pricingMode === "TRACK_UNITS"
                      ? t("settingsPage.priceBook.defaultRate")
                      : t("settingsPage.priceBook.defaultAmountShort")}
                  </Label>
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    value={priceBookForm.defaultPrice}
                    onChange={(e) =>
                      setPriceBookForm((prev) => ({
                        ...prev,
                        defaultPrice: e.target.value,
                      }))
                    }
                    placeholder="0.00"
                  />
                </div>
              </div>
            ) : null}

            {isOtherPriceBook ? (
              <div className="space-y-1.5">
                <Label>{t("settingsPage.priceBook.defaultAmount")}</Label>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={priceBookForm.defaultPrice}
                  onChange={(e) =>
                    setPriceBookForm((prev) => ({
                      ...prev,
                      pricingMode: "NO_UNITS",
                      unit: "",
                      defaultPrice: e.target.value,
                    }))
                  }
                  placeholder="0.00"
                />
              </div>
            ) : null}
          </div>
          <DialogFooter className="gap-2.5">
            <Button
              type="button"
              variant="outline"
              onClick={() => setPriceBookEditorOpen(false)}
              disabled={priceBookSaving}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              onClick={() => void savePriceBookItem()}
              disabled={priceBookSaving}
              className="bg-accent text-accent-foreground hover:bg-accent/90 gap-1.5"
            >
              {priceBookSaving ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Save className="h-3.5 w-3.5" />
              )}
              {t("common.saveChanges")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={emailTemplateEditorOpen}
        onOpenChange={setEmailTemplateEditorOpen}
      >
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {emailTemplateDraft?.id
                ? t("settingsPage.emailTemplates.editDialog")
                : t("settingsPage.emailTemplates.new")}
            </DialogTitle>
          </DialogHeader>
          {emailTemplateDraft ? (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2 space-y-1.5">
                  <Label>{t("settingsPage.emailTemplates.templateName")}</Label>
                  <Input
                    value={emailTemplateDraft.name}
                    onChange={(e) =>
                      setEmailTemplateDraft((prev) =>
                        prev ? { ...prev, name: e.target.value } : prev,
                      )
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("settingsPage.emailTemplates.subtype")}</Label>
                  {emailTemplateDraft.id ? (
                    <Input
                      value={
                        EMAIL_TEMPLATE_SUBTYPES.find(
                          (s) => s.value === emailTemplateDraft.subType,
                        )
                          ? getEmailSubtypeLabel(t, emailTemplateDraft.subType)
                          : emailTemplateDraft.subType
                      }
                      readOnly
                      className="bg-muted/20"
                    />
                  ) : (
                    <select
                      className="w-full h-9 rounded-md border bg-background px-3 text-sm"
                      value={emailTemplateDraft.subType}
                      onChange={(e) =>
                        setEmailTemplateDraft((prev) =>
                          prev
                            ? {
                                ...prev,
                                subType: e.target.value,
                              }
                            : prev,
                        )
                      }
                    >
                      {EMAIL_TEMPLATE_SUBTYPES.map((subType) => (
                        <option key={subType.value} value={subType.value}>
                          {getEmailSubtypeLabel(t, subType.value)}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>{t("settingsPage.emailTemplates.subject")}</Label>
                <Input
                  value={String(emailTemplateDraft.dataJson.subject ?? "")}
                  onFocus={() => setEmailFocus("subject")}
                  onChange={(e) =>
                    updateEmailDraftData({ subject: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label>{t("settingsPage.emailTemplates.body")}</Label>
                <Textarea
                  rows={8}
                  value={String(emailTemplateDraft.dataJson.body ?? "")}
                  onFocus={() => setEmailFocus("body")}
                  onChange={(e) =>
                    updateEmailDraftData({ body: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>
                  {t("settingsPage.emailTemplates.availableVariables")}
                </Label>
                <div className="flex flex-wrap gap-1.5">
                  {emailTemplateVariables.map((v) => (
                    <button
                      key={v}
                      type="button"
                      className="text-xs bg-muted border rounded px-2 py-1 font-mono"
                      onClick={() => insertEmailVariable(v)}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEmailTemplateEditorOpen(false)}
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={() => void saveEmailTemplate()}
              disabled={
                emailTemplateSaving || readOnly || starterEmailTemplatesLocked
              }
            >
              {emailTemplateSaving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : null}
              {t("common.saveChanges")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {hasChanges && !readOnly && (
        <div className="fixed bottom-0 left-0 right-0 bg-card border-t p-3 flex items-center justify-between z-30 animate-fade-in">
          <span className="text-sm text-muted-foreground">
            {t("settingsPage.unsavedChanges")}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (!company || !settings) return;
                const resetForm: FormState = {
                  companyName: company.name ?? "",
                  ownerFullName: company.ownerFullName ?? "",
                  companyType:
                    company.companyType ?? "GENERAL_CONTRACTOR_REMODELER",
                  companyEmail: company.email ?? "",
                  companyPhone: company.phone ?? "",
                  companyAddress: settings.companyAddress ?? "",
                  companyWebsite: company.website ?? "",
                  companyLogoUrl: settings.companyLogoUrl ?? "",
                  companySealSignatureUrl:
                    settings.companySealSignatureUrl ?? "",
                  pdfBrandTagline: settings.pdfBrandTagline ?? "",
                  pdfBrandColorHex: settings.pdfBrandColorHex ?? "#F59E0B",
                  currencyCode: settings.currencyCode ?? "USD",
                  changeOrderEnabled: settings.changeOrderEnabled,
                  tmTicketEnabled: settings.tmTicketEnabled,
                  extraWorkEnabled: settings.extraWorkEnabled,
                  ticketTypeOrder: settings.ticketTypeOrder?.length
                    ? settings.ticketTypeOrder
                    : ["tm", "ew", "co"],
                  coPrefix: settings.coPrefix,
                  tmPrefix: settings.tmPrefix,
                  extraWorkPrefix: settings.extraWorkPrefix,
                  coNumberFormat: settings.coNumberFormat,
                  tmNumberFormat: settings.tmNumberFormat,
                  ewNumberFormat: settings.ewNumberFormat,
                  coNextNumber: settings.coNextNumber,
                  tmNextNumber: settings.tmNextNumber,
                  ewNextNumber: settings.ewNextNumber,
                  autoRemindersEnabled: settings.reminderDayOffsets.length > 0,
                  defaultReminderCadenceDays:
                    settings.defaultReminderCadenceDays,
                  requireRejectionComment: settings.requireRejectionComment,
                  allowManualApprovalOverride:
                    settings.allowManualApprovalOverride,
                  requireSignatureToApprove: settings.requireSignatureToApprove,
                  autoDeletePendingApprovalEnabled:
                    settings.autoDeletePendingApprovalEnabled ?? false,
                  autoDeletePendingApprovalDays:
                    settings.autoDeletePendingApprovalDays ?? 10,
                  notifyTicketApproved: settings.notifyTicketApproved,
                  notifyTicketRejected: settings.notifyTicketRejected,
                  notifySignatureCaptured: settings.notifySignatureCaptured,
                  notifyReminderSent: settings.notifyReminderSent,
                  notifyPaymentFollowupSent:
                    settings.notifyPaymentFollowupSent ?? true,
                  notifyPaymentRecorded: settings.notifyPaymentRecorded ?? true,
                  notifyWeeklySummary: settings.notifyWeeklySummary,
                  scheduleAutoStartEnabled:
                    settings.scheduleAutoStartEnabled ?? true,
                  scheduleAutoCompleteEnabled:
                    settings.scheduleAutoCompleteEnabled ?? true,
                  scheduleWorkdayStartTime:
                    settings.scheduleWorkdayStartTime ?? "08:00",
                  scheduleWorkdayEndTime:
                    settings.scheduleWorkdayEndTime ?? "19:00",
                  approvalLinkExpiryDays: settings.approvalLinkExpiryDays,
                  defaultTaxRate: Number(settings.defaultTaxRate ?? 0),
                  twoFactorEnabled: settings.twoFactorEnabled,
                };
                setForm(resetForm);
                initialSnapshotRef.current = JSON.stringify(resetForm);
                setLogoDataUrl(null);
                setRemoveLogo(false);
                setSealSignatureDataUrl(null);
                setRemoveSealSignature(false);
              }}
              disabled={saving}
            >
              {t("settingsPage.discard")}
            </Button>
            <Button
              size="sm"
              className="bg-accent text-accent-foreground hover:bg-accent/90 gap-1.5 font-semibold"
              onClick={() => void saveChanges()}
              disabled={saving}
            >
              {saving ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Save className="h-3.5 w-3.5" />
              )}{" "}
              {t("common.saveChanges")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
